import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
const BidderGeoPanel = lazy(() => import('./BidderGeoPanel'));
import {
    List,
    Datagrid,
    DatagridConfigurable,
    TextField,
    DateField,
    EditButton,
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    Create,
    Show,
    TabbedShowLayout,
    Tab,
    ShowButton,
    useRecordContext,
    useTranslate,
    Filter,
    FilterLiveSearch,
    EmailField,
    UrlField,
    NumberField,
    FunctionField,
    useListController,
    ListContextProvider,
    useListContext,
    useRedirect,
    Button,
    ReferenceManyField,
    required,
    email,
    Admin,
    Resource,
    DateInput,
    SelectColumnsButton,
    TopToolbar,
    ExportButton,
    useNotify,
    useRefresh,
    useDataProvider,
    ListButton,
    useCreate,
    useGetList,
    Toolbar,
    SaveButton,
    useUpdate
} from 'react-admin';
import * as XLSX from 'xlsx';
import { Grid, Box, Typography, Divider, Chip, Card, CardContent, Stack, Dialog, DialogContent, DialogTitle, DialogActions, Alert, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, FormControl, FormLabel, MenuItem, Select, InputLabel, Button as MuiButton, TextField as MuiTextField, Checkbox, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';
import BlockIcon from '@mui/icons-material/Block';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useFormContext, useWatch } from 'react-hook-form';
import { BidderChecksList } from './components/BidderChecks';
import { BidderCheckDetails } from './components/BidderCheckDetails';
import { BidderTendersList } from './components/BidderTenders';
import {
    BidderInfoField,
    TenderInfoField,
    FinancialRiskField,
    OfferStatusField,
    WorkflowInfoField,
    MarketingConsentsField,
    StyledList,
    ListActions as LeadsListActions,
    BidderOfferLeadFilterSidebar
} from './bidderOfferLeads';

const CONSENT_TYPES = ['email', 'phone', 'group_company'];

const CONSENT_FIELD_MAP = {
    email: 'consent_email',
    phone: 'consent_phone',
    group_company: 'consent_group_company'
};

const normalizeConsents = (payload = {}) => {
    const normalized = {};
    CONSENT_TYPES.forEach(type => {
        const entry = payload[type];
        if (entry && typeof entry === 'object' && entry !== null && 'given' in entry) {
            normalized[type] = Boolean(entry.given);
        } else if (typeof entry === 'boolean') {
            normalized[type] = entry;
        } else if (typeof entry === 'string') {
            normalized[type] = entry === 'true' || entry === '1';
        } else if (typeof entry === 'number') {
            normalized[type] = entry === 1;
        } else {
            normalized[type] = false;
        }
    });
    return normalized;
};

const extractConsentValues = (data = {}) => {
    const values = {};
    CONSENT_TYPES.forEach(type => {
        const field = CONSENT_FIELD_MAP[type];
        if (field in data) {
            values[type] = Boolean(data[field]);
        }
    });
    return values;
};

const stripConsentFields = (data = {}) => {
    const sanitized = { ...data };
    Object.values(CONSENT_FIELD_MAP).forEach(field => {
        if (field in sanitized) {
            delete sanitized[field];
        }
    });
    return sanitized;
};

const diffConsents = (initial = {}, current = {}) =>
    CONSENT_TYPES.reduce((changes, type) => {
        if (initial[type] !== current[type]) {
            changes.push({ consent_type: type, given: current[type] });
        }
        return changes;
    }, []);

const postConsentUpdate = async (bidderId, consentType, given) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${apiUrl}/bidders/${bidderId}/update_consent/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        },
        body: JSON.stringify({
            consent_type: consentType,
            given
        })
    });

    if (!response.ok) {
        let errorMessage = 'Nie udało się zapisać zgody marketingowej';
        try {
            const errorData = await response.json();
            if (errorData?.error) {
                errorMessage = errorData.error;
            }
        } catch (error) {
            // ignore parse errors
        }
        throw new Error(errorMessage);
    }
};

// Component to display financial risk for bidders (not bidder offers)
const BidderFinancialRiskField = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    const details = record.details;
    if (!details) {
        return (
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
                Brak danych
            </Typography>
        );
    }
    
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {details.financial_risk_rating ? (
                <Tooltip 
                    title={details.financial_risk_description || ''}
                    arrow
                    placement="top"
                >
                    <Chip 
                        label={details.financial_risk_rating}
                        color={details.financial_risk_color || 'default'}
                        size="small"
                        sx={{ fontSize: '0.75rem', height: '24px', cursor: 'help' }}
                    />
                </Tooltip>
            ) : (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
                    Brak danych
                </Typography>
            )}
        </Box>
    );
};

// BidderPWGDetails Component
// Komponent do wyświetlania danych finansowych
const FinancialDataTable = ({ data }) => {
    // Filtruj tylko pozycje najwyższego poziomu (np. 01.01, 01.02, 03.01)
    const topLevelItems = data.filter(item => {
        const ident = item.ident;
        // Pozycje najwyższego poziomu mają format XX.XX (bez dodatkowych kropek)
        return ident && ident.split('.').length === 2;
    });

    // Znajdź wszystkie dostępne lata w danych
    const availableYears = [...new Set(topLevelItems.map(item => item.year))];
    
    // Posortuj lata malejąco i weź 3 ostatnie
    const sortedYears = availableYears
        .sort((a, b) => parseInt(b) - parseInt(a))
        .slice(0, 3);

    // Grupuj dane według identyfikatora
    const groupedData = topLevelItems.reduce((acc, item) => {
        const key = item.ident;
        if (!acc[key]) {
            acc[key] = {
                label: item.label,
                ident: item.ident,
                years: {}
            };
        }
        acc[key].years[item.year] = item.amount;
        return acc;
    }, {});

    // Konwertuj na tablicę i posortuj
    const sortedData = Object.values(groupedData).sort((a, b) => {
        // Sortuj według identyfikatora
        return a.ident.localeCompare(b.ident);
    });

    // Funkcja do formatowania kwot
    const formatAmount = (amount) => {
        if (!amount || amount === '0.00') return '-';
        const num = parseFloat(amount);
        return new Intl.NumberFormat('pl-PL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    };

    return (
        <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                            Pozycja
                        </TableCell>
                        {sortedYears.map(year => (
                            <TableCell key={year} align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                {year} (PLN)
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedData.map((item, index) => {
                        // Sprawdź czy to wiersz podsumowujący
                        const isSummaryRow = item.label === 'Aktywa razem' || item.label === 'Pasywa razem';
                        
                        return (
                            <TableRow 
                                key={index} 
                                hover={!isSummaryRow}
                                sx={{
                                    backgroundColor: isSummaryRow ? '#f8f9fa' : 'inherit',
                                    borderTop: isSummaryRow ? '2px solid #dee2e6' : 'none',
                                    '&:hover': isSummaryRow ? {
                                        backgroundColor: '#e9ecef'
                                    } : {}
                                }}
                            >
                                <TableCell sx={{ 
                                    fontWeight: isSummaryRow ? 'bold' : 500,
                                    fontSize: isSummaryRow ? '0.95rem' : 'inherit',
                                    color: isSummaryRow ? '#495057' : 'inherit'
                                }}>
                                    {item.label}
                                </TableCell>
                                {sortedYears.map(year => (
                                    <TableCell 
                                        key={year} 
                                        align="right"
                                        sx={{
                                            fontWeight: isSummaryRow ? 'bold' : 'normal',
                                            fontSize: isSummaryRow ? '0.95rem' : 'inherit',
                                            color: isSummaryRow ? '#495057' : 'inherit'
                                        }}
                                    >
                                        {formatAmount(item.years[year])}
                                    </TableCell>
                                ))}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const BidderPWGDetails = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    
    if (!record) return null;
    
    const details = record.details;
    
    if (!details) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body1">
                    Brak szczegółowych danych PWG dla tego biddera.
                </Typography>
            </Box>
        );
    }
    
    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={4}>
                {/* Lewa strona - obecne dane */}
                <Grid item xs={12} lg={6}>
                    <Box sx={{ pr: 2 }}>
                        {/* Opis błędu walidacji PWG */}
                        {details.pwg_error_description && (
                            <Box sx={{ mt: 2, mb: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    Błąd walidacji PWG
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Box sx={{ 
                                    backgroundColor: '#ffebee', 
                                    border: '1px solid #f44336', 
                                    borderRadius: 1, 
                                    p: 2
                                }}>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'flex-start', 
                                        gap: 1,
                                        mb: 1
                                    }}>
                                        <Typography variant="h6" sx={{ 
                                            color: '#d32f2f',
                                            fontSize: '1.2rem'
                                        }}>
                                            ⚠️
                                        </Typography>
                                        <Typography variant="subtitle1" sx={{ 
                                            fontWeight: 600, 
                                            color: '#d32f2f',
                                            flex: 1
                                        }}>
                                            Firma nie spełnia wymagań PWG
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ 
                                        color: '#d32f2f',
                                        lineHeight: 1.5,
                                        whiteSpace: 'pre-wrap'
                                    }}>
                                        {details.pwg_error_description}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Oceny PWG */}
                        <Box sx={{ mt: 2, mb: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                Oceny PWG
                            </Typography>
                            <Divider sx={{ mb: 3 }} />
                            
                            {/* Skala ryzyka finansowego */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                                    Skala ryzyka finansowego
                                </Typography>
                                {details.financial_risk_rating ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Chip 
                                            label={details.financial_risk_rating} 
                                            color={details.financial_risk_color}
                                            size="medium"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            {details.financial_risk_description}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Brak oceny ryzyka finansowego
                                    </Typography>
                                )}
                            </Box>
                            
                            {/* Skala wiarygodności firmy */}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                                    Skala wiarygodności firmy
                                </Typography>
                                {details.company_credibility_rating ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Chip 
                                            label={details.company_credibility_rating} 
                                            color={details.company_credibility_color}
                                            size="medium"
                                            sx={{ fontWeight: 'bold' }}
                                        />
                                        <Typography variant="body2" color="text.secondary">
                                            {details.company_credibility_description}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Brak oceny wiarygodności firmy
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Beneficjenci rzeczywiści */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                Beneficjenci rzeczywiści
                            </Typography>
                            <Divider sx={{ mb: 3 }} />
                            {details.has_beneficial_owners ? (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                        Liczba beneficjentów: {details.beneficial_owners_count}
                                    </Typography>
                                    <TableContainer component={Paper} sx={{ maxHeight: '400px', overflow: 'auto' }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Imię</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Drugie imię</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Nazwisko</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Kod kraju</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Obywatelstwo</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5' }}>Data urodzenia</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Array.isArray(details.beneficial_owners?.benef) && details.beneficial_owners.benef.length > 0 ? (
                                                    details.beneficial_owners.benef.map((beneficiary, index) => (
                                                        <TableRow key={index} hover>
                                                            <TableCell>{beneficiary.imie || '-'}</TableCell>
                                                            <TableCell>{beneficiary.imie2 || '-'}</TableCell>
                                                            <TableCell>{beneficiary.nazwisko || '-'}</TableCell>
                                                            <TableCell>{beneficiary.kod_kraju || '-'}</TableCell>
                                                            <TableCell>{beneficiary.obywatelstwo || '-'}</TableCell>
                                                            <TableCell>{beneficiary.data_urodzenia || '-'}</TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                                                            Brak danych
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Brak danych o beneficjentach rzeczywistych
                                </Typography>
                            )}
                        </Box>
                        
                        {/* Alerty */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                Alerty
                            </Typography>
                            <Divider sx={{ mb: 3 }} />
                            {details.has_alerts ? (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                                        Wykryto {Array.isArray(details.alerts) ? details.alerts.length : Object.keys(details.alerts).length} alertów:
                                    </Typography>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexWrap: 'wrap', 
                                        gap: 1,
                                        maxHeight: '200px',
                                        overflow: 'auto'
                                    }}>
                                        {(() => {
                                            // Funkcja do tłumaczenia alertów na czytelne opisy
                                            const getAlertDescription = (alert) => {
                                                const alertDescriptions = {
                                                    'upadlosc': 'Upadłość',
                                                    'upadlosc_msig': 'Upadłość (MSIG)',
                                                    'zabezpieczenie_msig': 'Zabezpieczenie (MSIG)',
                                                    'post_ukladowe_msig': 'Postępowanie układowe (MSIG)',
                                                    'post_rest_napr': 'Postępowanie restrukturyzacyjne',
                                                    'sankcje': 'Sankcje',
                                                    'zablokowanie': 'Zablokowanie',
                                                    'wstrzymanie': 'Wstrzymanie działalności',
                                                    'likwidacja': 'Likwidacja',
                                                    'rozwiązanie': 'Rozwiązanie spółki'
                                                };
                                                return alertDescriptions[alert] || alert;
                                            };

                                            // Obsługa różnych formatów alertów
                                            let alertsToDisplay = [];
                                            if (Array.isArray(details.alerts)) {
                                                alertsToDisplay = details.alerts.filter(alert => alert && alert.trim());
                                            } else if (typeof details.alerts === 'object' && details.alerts !== null) {
                                                alertsToDisplay = Object.values(details.alerts).filter(alert => alert && alert.trim());
                                            }

                                            return alertsToDisplay.map((alert, index) => (
                                                <Chip
                                                    key={index}
                                                    label={getAlertDescription(alert)}
                                                    color="error"
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ 
                                                        fontWeight: 500,
                                                        borderColor: '#f44336',
                                                        color: '#d32f2f',
                                                        '&:hover': {
                                                            backgroundColor: '#ffebee',
                                                            borderColor: '#d32f2f'
                                                        }
                                                    }}
                                                />
                                            ));
                                        })()}
                                    </Box>
                                    <Typography variant="caption" sx={{ 
                                        display: 'block', 
                                        mt: 1, 
                                        color: 'text.secondary',
                                        fontStyle: 'italic'
                                    }}>
                                        ⚠️ Te alerty mogą wskazywać na problemy finansowe lub prawne firmy
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1,
                                    color: 'success.main'
                                }}>
                                    <Typography variant="body2">
                                        ✅ Brak alertów
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        {/* Dane o przetargach */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                Dane o przetargach
                            </Typography>
                            <Divider sx={{ mb: 3 }} />
                            {details.pwg_przetargi ? (
                                <Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box sx={{ 
                                                backgroundColor: '#f8f9fa', 
                                                border: '1px solid #e9ecef', 
                                                borderRadius: 1, 
                                                p: 2,
                                                textAlign: 'center'
                                            }}>
                                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                    {details.pwg_przetargi.ile || '0'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Liczba przetargów
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Box sx={{ 
                                                backgroundColor: '#f8f9fa', 
                                                border: '1px solid #e9ecef', 
                                                borderRadius: 1, 
                                                p: 2,
                                                textAlign: 'center'
                                            }}>
                                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                    {details.pwg_przetargi.wartosc ? 
                                                        new Intl.NumberFormat('pl-PL', {
                                                            minimumFractionDigits: 0,
                                                            maximumFractionDigits: 0
                                                        }).format(parseFloat(details.pwg_przetargi.wartosc)) + ' PLN' : 
                                                        '0 PLN'
                                                    }
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Wartość przetargów
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Brak danych o przetargach
                                </Typography>
                            )}
                        </Box>

                        {/* Informacje o danych */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                Informacje o danych
                            </Typography>
                            <Divider sx={{ mb: 3 }} />
                            <Grid container spacing={1}>
                                <Grid item xs={5} sx={{ color: 'text.secondary' }}>Data utworzenia</Grid>
                                <Grid item xs={7}>
                                    {details.created_at ? new Date(details.created_at).toLocaleString() : 'Brak'}
                                </Grid>
                                <Grid item xs={5} sx={{ color: 'text.secondary' }}>Data aktualizacji</Grid>
                                <Grid item xs={7}>
                                    {details.updated_at ? new Date(details.updated_at).toLocaleString() : 'Brak'}
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Grid>

                {/* Prawa strona - dane finansowe */}
                <Grid item xs={12} lg={6}>
                    <Box sx={{ pl: 2 }}>
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                Dane finansowe
                            </Typography>
                            <Divider sx={{ mb: 3 }} />
                            {details.financial_data && details.financial_data.length > 0 ? (
                                <FinancialDataTable data={details.financial_data} />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Brak danych finansowych
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

// EmailStatusField component for emails (same as in emails.js)
const EmailStatusField = props => {
    const record = useRecordContext();
    if (!record) return null;

    const getStatusColor = status => {
        switch (status) {
            case 'sent':
                return { color: 'success', label: 'Wysłany' };
            case 'not_sent':
                return { color: 'warning', label: 'Nie wysłany' };
            case 'failed':
                return { color: 'error', label: 'Błąd' };
            default:
                return { color: 'default', label: status };
        }
    };

    const { color, label } = getStatusColor(record.status);

    return (
        <Chip 
            label={label}
            color={color}
            size="small"
        />
    );
};

// Bidder Email Create Modal Component
const BidderEmailCreateModal = ({ open, onClose, bidderId, bidderEmail, onSuccess }) => {
    const [create] = useCreate();
    const notify = useNotify();
    const translate = useTranslate();
    const [formData, setFormData] = useState({
        subject: '',
        to_email: bidderEmail || '',
        body: ''
    });
    const [errors, setErrors] = useState({});
    
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };
    
    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.subject.trim()) {
            newErrors.subject = 'Temat jest wymagany';
        }
        
        if (!formData.body.trim()) {
            newErrors.body = 'Treść jest wymagana';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('Form data:', formData);
        console.log('Bidder email:', bidderEmail);
        console.log('Bidder ID:', bidderId);
        
        if (!validateForm()) {
            return;
        }
        
        const emailData = {
            ...formData,
            to_email: bidderEmail, // Automatycznie ustaw email biddera
            bidder: parseInt(bidderId)
        };
        
        console.log('Email data to send:', emailData);
        
        try {
            await create('email/messages', { data: emailData });
            notify('Email został utworzony pomyślnie', { type: 'success' });
            // Reset form
            setFormData({
                subject: '',
                to_email: bidderEmail || '',
                body: ''
            });
            setErrors({});
            onSuccess();
        } catch (error) {
            console.error('Error creating email:', error);
            notify(`Błąd: ${error.message}`, { type: 'error' });
        }
    };
    
    const handleClose = () => {
        // Reset form when closing
        setFormData({
            subject: '',
            to_email: bidderEmail || '',
            body: ''
        });
        setErrors({});
        onClose();
    };
    
    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>Nowy Email</DialogTitle>
            <DialogContent>
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                    {bidderEmail && (
                        <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Email będzie wysłany do:
                            </Typography>
                            <Typography variant="body2" color="primary">
                                {bidderEmail}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            {translate('resources.email.messages.fields.subject')} *
                        </Typography>
                        <input
                            type="text"
                            value={formData.subject}
                            onChange={(e) => handleInputChange('subject', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: errors.subject ? '1px solid #d32f2f' : '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                            placeholder="Wprowadź temat emaila"
                        />
                        {errors.subject && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.subject}
                            </Typography>
                        )}
                    </Box>
                    
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            {translate('resources.email.messages.fields.body')} *
                        </Typography>
                        <textarea
                            value={formData.body}
                            onChange={(e) => handleInputChange('body', e.target.value)}
                            rows={10}
                            style={{
                                width: '100%',
                                padding: '12px',
                                border: errors.body ? '1px solid #d32f2f' : '1px solid #ccc',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontFamily: 'inherit',
                                resize: 'vertical'
                            }}
                            placeholder="Wprowadź treść emaila"
                        />
                        {errors.body && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                {errors.body}
                            </Typography>
                        )}
                    </Box>
                    
                    <DialogActions>
                        <Button onClick={handleClose} color="inherit">
                            Anuluj
                        </Button>
                        <Button type="submit" variant="contained" color="primary">
                            Wyślij Email
                        </Button>
                    </DialogActions>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

// Custom Email Link Component
const EmailLinkField = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    
    if (!record) return null;

    const handleClick = (e) => {
        e.preventDefault();
        redirect('show', 'email/messages', record.id);
    };

    return (
        <Link 
            component="button"
            onClick={handleClick}
            underline="hover"
            color="primary"
            style={{ 
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit'
            }}
        >
            {translate('ra.action.show', 'Pokaż')}
        </Link>
    );
};

// Bidder Emails List Component
const BidderEmailsDatagrid = () => {
    const { data, isLoading } = useListContext();
    const translate = useTranslate();

    if (isLoading || !data) return null;

    return (
        <Datagrid 
            bulkActionButtons={false}
            rowClick={false}
        >
            <TextField source="subject" label={translate('resources.email.messages.fields.subject')} />
            <EmailField source="to_email" label={translate('resources.email.messages.fields.to_email')} />
            <EmailStatusField source="status" label={translate('resources.email.messages.fields.status')} />
            <TextField source="source" label={translate('resources.email.messages.fields.source')} />
            <DateField source="created_at" label={translate('resources.email.messages.fields.created_at')} showTime />
            <DateField source="sent_at" label={translate('resources.email.messages.fields.sent_at')} showTime />
            <EmailLinkField />
        </Datagrid>
    );
};

const BidderEmailsList = () => {
    const record = useRecordContext();
    const resource = record ? `bidders/${record.id}/messages` : null;
    const listContext = useListController({
        resource,
        sort: { field: 'created_at', order: 'DESC' },
        perPage: 25,
        disableSyncWithLocation: true,
    });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const translate = useTranslate();
    const notify = useNotify();

    if (!record) return null;
    if (listContext.isLoading) return null;
    if (listContext.error) return <div>Error loading emails</div>;

    const handleSuccess = () => {
        setShowCreateModal(false);
        // Usunięte odświeżanie strony
    };

    const handleOpenModal = () => {
        const problems = [];
        
        if (!record.email) {
            problems.push('Bidder nie ma adresu email');
        }
        
        if (record.status === 'BLOCKED') {
            problems.push('Bidder jest zablokowany');
        }
        
        if (record.status === 'DELETED') {
            problems.push('Bidder jest usunięty');
        }
        
        if (problems.length > 0) {
            notify(problems.join(', '), { type: 'warning' });
            return;
        }
        
        setShowCreateModal(true);
    };

    return (
        <ListContextProvider value={listContext}>
            <div style={{ marginTop: '1em' }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button 
                        variant="contained" 
                        color="primary"
                        onClick={handleOpenModal}
                        startIcon={<AddIcon />}
                    >
                        {translate('resources.email.messages.actions.new_email', 'Nowy Email')}
                    </Button>
                </Box>
                <BidderEmailsDatagrid />
                {showCreateModal && (
                    <BidderEmailCreateModal 
                        open={showCreateModal}
                        onClose={() => setShowCreateModal(false)}
                        bidderId={record.id}
                        bidderEmail={record.email}
                        onSuccess={handleSuccess}
                    />
                )}
            </div>
        </ListContextProvider>
    );
};

// Custom Status Field component with color coding
const BlockedInfo = () => {
    const record = useRecordContext();
    if (!record || record.status !== 'BLOCKED') return null;
    return (
        <>
            <Grid item xs={5} sx={{ color: 'text.secondary' }}>Zablokowany dnia</Grid>
            <Grid item xs={7}>
                <Typography variant="body2">
                    {record.blocked_at ? new Date(record.blocked_at).toLocaleString('pl-PL') : '—'}
                </Typography>
            </Grid>
            <Grid item xs={5} sx={{ color: 'text.secondary' }}>Zablokowany przez</Grid>
            <Grid item xs={7}>
                <Typography variant="body2">
                    {record.blocked_by_name || '—'}
                </Typography>
            </Grid>
        </>
    );
};

const StatusField = ({ source }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record || !record[source]) return null;
    
    const status = record[source];
    let color = 'default';
    let label = status;
    
    switch (status) {
        case 'ACTIVE':
            color = 'success';
            label = translate('resources.bidders.fields.status_active', 'Active');
            break;
        case 'BLOCKED':
            color = 'error';
            label = translate('resources.bidders.fields.status_blocked', 'Blocked');
            break;
        case 'DELETED':
            color = 'default';
            label = translate('resources.bidders.fields.status_deleted', 'Deleted');
            break;
        default:
            color = 'default';
            label = status;
    }
    
    return <Chip label={label} color={color} size="small" />;
};

// Custom field for type with color
const TypeField = ({ source }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;
    const value = record[source];
    if (!value) return null;
    
    let color = 'default';
    let label = value;
    
    switch (value) {
        case 'niski':
            color = 'info';
            label = translate('resources.bidders.fields.type_niski', 'Niski');
            break;
        case 'średni':
            color = 'warning';
            label = translate('resources.bidders.fields.type_średni', 'Średni');
            break;
        case 'wysoki':
            color = 'success';
            label = translate('resources.bidders.fields.type_wysoki', 'Wysoki');
            break;
        default:
            color = 'default';
            label = value;
    }
    
    return <Chip label={label} color={color} size="small" />;
};

// Custom field for pkd_others with limited display
const PkdOthersField = ({ source }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return null;
    
    const pkdString = record[source];
    const pkdCodes = pkdString.split(' ').filter(code => code.trim() !== '');
    
    if (pkdCodes.length === 0) return null;
    
    const displayCount = 5;
    const displayCodes = pkdCodes.slice(0, displayCount);
    const remainingCount = pkdCodes.length - displayCount;
    
    if (remainingCount <= 0) {
        // If 5 or fewer codes, just display them
        return <Typography variant="body2">{pkdCodes.join(' ')}</Typography>;
    }
    
    // If more than 5, show first 5 and add tooltip with all remaining
    const remainingCodes = pkdCodes.slice(displayCount);
    
    return (
        <Typography variant="body2">
            {displayCodes.join(' ')}{' '}
            <Tooltip 
                title={
                    <Box sx={{ maxHeight: '300px', overflow: 'auto' }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Pozostałe kody ({remainingCount}):
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {remainingCodes.join(' ')}
                        </Typography>
                    </Box>
                }
                arrow
                placement="top"
            >
                <span style={{ 
                    color: '#1976d2', 
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontStyle: 'italic'
                }}>
                    ...i {remainingCount} więcej
                </span>
            </Tooltip>
        </Typography>
    );
};

// Custom field for is_winner with color
const IsWinnerField = (props) => {
    const record = useRecordContext();
    const translate = useTranslate();
    if (!record) return null;
    const value = record.is_winner;
    if (value === true || value === 'true' || value === 1 || value === '1') {
        return <Chip label={translate('resources.tenders.fields.is_winner') + ': TAK'} color="success" size="small" />;
    } else if (value === false || value === 'false' || value === 0 || value === '0') {
        return <Chip label={translate('resources.tenders.fields.is_winner') + ': NIE'} color="error" size="small" />;
    } else {
        return <Chip label={translate('resources.tenders.fields.is_winner') + ': ?'} size="small" />;
    }
};

const StyledSimpleForm = styled(SimpleForm)(({ theme }) => ({
    backgroundColor: '#fff',
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '& .MuiFormControl-root': {
        width: '100%',
        maxWidth: '600px',
        marginBottom: '16px'
    },
    '& .MuiInputBase-root': {
        width: '100%'
    }
}));

// Style the show layout to format fields
const StyledTabbedShowLayout = styled(TabbedShowLayout)(({ theme }) => ({
    '& .RaLabeled-label': {
        display: 'inline-block',
        width: '200px',
        marginRight: '8px',
        fontWeight: 'bold',
        verticalAlign: 'baseline',  // Changed to baseline alignment
        lineHeight: '1.5',  // Consistent line height
        '&:after': {
            content: '":"',
        }
    },
    '& .RaLabeled-value': {
        display: 'inline-block',
        verticalAlign: 'baseline',  // Changed to baseline alignment
        lineHeight: '1.5'  // Consistent line height
    },
    '& .RaLabeled-wrapper': {
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'baseline',  // Changed to baseline alignment
        minHeight: '24px'  // Ensure consistent minimum height
    }
}));

const BidderFilter = (props) => {
    const translate = useTranslate();
    return (
        <Filter {...props}>
            <FilterLiveSearch source="name" label={translate('resources.bidders.fields.name')} />
            <FilterLiveSearch source="tax_number" label={translate('resources.bidders.fields.tax_number')} />
            <FilterLiveSearch source="registration_number" label={translate('resources.bidders.fields.registration_number')} />
            <FilterLiveSearch source="city" label={translate('resources.bidders.fields.city')} />
            <SelectInput 
                source="status" 
                label={translate('resources.bidders.fields.status')} 
                choices={[
                    { id: 'ACTIVE', name: translate('resources.bidders.fields.status_active', 'Active') },
                    { id: 'BLOCKED', name: translate('resources.bidders.fields.status_blocked', 'Blocked') },
                    { id: 'DELETED', name: translate('resources.bidders.fields.status_deleted', 'Deleted') }
                ]}
                alwaysOn
            />
        </Filter>
    );
};

// Custom field components for show view
const LabeledField = ({ source }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return null;
    return <span>{record[source]}</span>;
};

const LabeledEmailField = ({ source }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return null;
    return <a href={`mailto:${record[source]}`}>{record[source]}</a>;
};

const LabeledUrlField = ({ source }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return null;
    return <a href={record[source]} target="_blank" rel="noopener noreferrer">{record[source]}</a>;
};

const LabeledNumberField = ({ source, options }) => {
    const record = useRecordContext();
    if (!record || record[source] == null) return null;
    const formattedValue = new Intl.NumberFormat(undefined, options).format(record[source]);
    return <span>{formattedValue}</span>;
};

const LabeledDateField = ({ source, showTime }) => {
    const record = useRecordContext();
    if (!record || !record[source]) return null;
    const date = new Date(record[source]);
    const formattedDate = showTime 
        ? date.toLocaleString()
        : date.toLocaleDateString();
    return <span>{formattedDate}</span>;
};

// Customer Status Alert component
const CustomerStatusAlert = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    
    if (!record || !record.is_current_customer) return null;
    
    return (
        <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ 
                mb: 2,
                fontWeight: 'bold',
                backgroundColor: '#fff3e0',
                border: '1px solid #ffb74d',
                '& .MuiAlert-icon': {
                    color: '#f57c00'
                }
            }}
        >
            {translate('resources.bidders.customer_status.is_customer')}
        </Alert>
    );
};

const TenderShowButton = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    if (!record) return null;
    return (
        <Button
            label={translate('ra.action.show')}
            onClick={e => {
                e.stopPropagation();
                redirect('show', 'tenders', record.id);
            }}
        />
    );
};

const ExportExcelButton = ({ fixedFilters = {} }) => {
  const { filterValues, sort } = useListContext();
  const [loading, setLoading] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();

  const handleExport = async () => {
    setLoading(true);
    try {
      // Build query parameters to match current filters and sorting
      const params = new URLSearchParams();
      
      // Add filters (permanent fixed filters take precedence over user filters)
      const mergedFilters = { ...(filterValues || {}), ...fixedFilters };
      Object.entries(mergedFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
      
      // Add sorting
      if (sort?.field) {
        params.append('_sort', sort.field);
        params.append('_order', sort.order?.toLowerCase() || 'desc');
      }
      
      // Make direct API call to get all bidders (up to 2000)
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(`${apiUrl}/bidders/export/?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Export failed');
      }
      
      const result = await response.json();
      const data = result.results;
      
      if (!data || data.length === 0) {
        notify(translate('resources.bidders.export.no_data'), { type: 'warning' });
        return;
      }
      
      // Create Excel file
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Bidders');
      XLSX.writeFile(workbook, `bidders_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      notify(translate('resources.bidders.export.success', { count: data.length }), { type: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      notify(translate('resources.bidders.export.error'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button 
      label={loading ? translate('resources.bidders.export.exporting') : translate('resources.bidders.export.excel')} 
      onClick={handleExport}
      disabled={loading}
      startIcon={<FileDownloadIcon />}
      sx={{
        backgroundColor: '#E31837',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#C41430',
        },
        '& .MuiSvgIcon-root': {
          color: '#ffffff',
        },
      }}
    />
  );
};

const BlockButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();
    const [showConfirm, setShowConfirm] = React.useState(false);

    if (!record) return null;

    const handleClick = () => {
        setShowConfirm(true);
    };

    const handleConfirm = async () => {
        try {
            const newStatus = record.status === 'BLOCKED' ? 'ACTIVE' : 'BLOCKED';
            await dataProvider.updateBidderStatus('bidders', {
                id: record.id,
                data: { status: newStatus }
            });
            
            const action = newStatus === 'BLOCKED' ? 'zablokowany' : 'odblokowany';
            notify(`Oferent został ${action}`, { type: 'success' });
            refresh();
        } catch (error) {
            notify('Wystąpił błąd podczas zmiany statusu', { type: 'error' });
        }
        setShowConfirm(false);
    };

    const handleCancel = () => {
        setShowConfirm(false);
    };

    const isBlocked = record.status === 'BLOCKED';
    const role = localStorage.getItem('user_role');
    const isAdmin = role === 'admin';

    if (!isAdmin) return null;

    return (
        <>
            <Button
                onClick={handleClick}
                label={isBlocked ? translate('resources.bidders.actions.unblock') : translate('resources.bidders.actions.block')}
                startIcon={isBlocked ? <LockOpenIcon /> : <BlockIcon />}
            />
            <Dialog
                open={showConfirm}
                onClose={handleCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#fff',
                        padding: '24px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                <DialogTitle sx={{ padding: '0 0 16px 0', color: '#333333', fontSize: '1.25rem', fontWeight: 500 }}>
                    {isBlocked ? translate('resources.bidders.actions.unblock_confirm') : translate('resources.bidders.actions.block_confirm')}
                </DialogTitle>
                <DialogContent sx={{ padding: '0 0 24px 0', color: '#666666' }}>
                    <Typography>
                        {isBlocked 
                            ? `${translate('resources.bidders.actions.unblock_message')} "${record.name}"?` 
                            : `${translate('resources.bidders.actions.block_message')} "${record.name}"?`
                        }
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: 0 }}>
                    <Button onClick={handleCancel} sx={{ textTransform: 'none', fontWeight: 500 }}>
                        Anuluj
                    </Button>
                    <Button onClick={handleConfirm} variant="contained" color="primary" sx={{ textTransform: 'none', fontWeight: 500 }}>
                        Potwierdź
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const DeleteButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const translate = useTranslate();
    const [showConfirm, setShowConfirm] = React.useState(false);

    if (!record) return null;

    const handleClick = () => {
        setShowConfirm(true);
    };

    const handleConfirm = async () => {
        try {
            await dataProvider.updateBidderStatus('bidders', {
                id: record.id,
                data: { status: 'DELETED' }
            });
            
            notify('Oferent został usunięty', { type: 'success' });
            refresh();
        } catch (error) {
            notify('Wystąpił błąd podczas usuwania oferenta', { type: 'error' });
        }
        setShowConfirm(false);
    };

    const handleCancel = () => {
        setShowConfirm(false);
    };

    const role = localStorage.getItem('user_role');
    const isAdmin = role === 'admin';

    if (!isAdmin) return null;

    return (
        <>
            <Button
                onClick={handleClick}
                label={translate('resources.bidders.actions.delete')}
                startIcon={<DeleteIcon />}
            />
            <Dialog
                open={showConfirm}
                onClose={handleCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: '#fff',
                        padding: '24px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    }
                }}
            >
                <DialogTitle sx={{ padding: '0 0 16px 0', color: '#333333', fontSize: '1.25rem', fontWeight: 500 }}>
                    {translate('resources.bidders.actions.delete_confirm')}
                </DialogTitle>
                <DialogContent sx={{ padding: '0 0 24px 0', color: '#666666' }}>
                    <Typography>
                        {`${translate('resources.bidders.actions.delete_message')} "${record.name}"?`}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ padding: 0 }}>
                    <Button onClick={handleCancel} sx={{ textTransform: 'none', fontWeight: 500 }}>
                        Anuluj
                    </Button>
                    <Button onClick={handleConfirm} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 500 }}>
                        Usuń
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

const BidderShowActions = () => {
    const translate = useTranslate();
    
    return (
        <TopToolbar>
            <ListButton />
            <EditButton />
            <BlockButton />
            <DeleteButton />
        </TopToolbar>
    );
};

const ListActions = ({ fixedFilters = {} }) => {
  return (
    <TopToolbar>
      <SelectColumnsButton />
      <ExportExcelButton fixedFilters={fixedFilters} />
    </TopToolbar>
  );
};

const BidderFilterSidebar = ({ showBlockedAtFilter = false }) => {
  const translate = useTranslate();
  const { setFilters, filterValues } = useListContext();

  const [filterKey, setFilterKey] = useState(Date.now());
  const [consentEmail, setConsentEmail] = useState(filterValues.consent_email === true || filterValues.consent_email === 'true' ? 'true' : (filterValues.consent_email === false || filterValues.consent_email === 'false' ? 'false' : ''));
  const [consentPhone, setConsentPhone] = useState(filterValues.consent_phone === true || filterValues.consent_phone === 'true' ? 'true' : (filterValues.consent_phone === false || filterValues.consent_phone === 'false' ? 'false' : ''));
  const [consentGroupCompany, setConsentGroupCompany] = useState(filterValues.consent_group_company === true || filterValues.consent_group_company === 'true' ? 'true' : (filterValues.consent_group_company === false || filterValues.consent_group_company === 'false' ? 'false' : ''));
  const [consentEmailDateFrom, setConsentEmailDateFrom] = useState(filterValues.consent_email_date_from || '');
  const [consentEmailDateTo, setConsentEmailDateTo] = useState(filterValues.consent_email_date_to || '');
  const [bidderStatus, setBidderStatus] = useState(filterValues.status || '');
  const [blockedAtFrom, setBlockedAtFrom] = useState(filterValues.blocked_at_from || '');
  const [blockedAtTo, setBlockedAtTo] = useState(filterValues.blocked_at_to || '');
  const [financialRisk, setFinancialRisk] = useState(() => {
    if (filterValues.financial_risk_rating) {
      return filterValues.financial_risk_rating.split(',').map(v => v.trim()).filter(v => v);
    }
    return [];
  });

  const getSelectValue = () => {
    if (filterValues.is_current_customer === undefined || filterValues.is_current_customer === null) {
      return '';
    }
    return filterValues.is_current_customer === true || filterValues.is_current_customer === 'true' ? 'true' : 'false';
  };

  useEffect(() => {
    const hasAnyFilter = 
      filterValues.name || 
      filterValues.tax_number || 
      filterValues.registration_number ||
      filterValues.krs ||
      filterValues.pkd ||
      filterValues.pkd_others ||
      filterValues.city ||
      filterValues.municipality ||
      filterValues.province ||
      filterValues.is_current_customer !== undefined ||
      filterValues.consent_email !== undefined ||
      filterValues.consent_phone !== undefined ||
      filterValues.consent_group_company !== undefined ||
      filterValues.consent_email_date_from ||
      filterValues.consent_email_date_to ||
      filterValues.financial_risk_rating ||
      filterValues.status;
    
    if (!hasAnyFilter && Object.keys(filterValues).length === 0) {
      setFilterKey(Date.now());
    }
  }, [filterValues]);

  useEffect(() => {
    if (filterValues.financial_risk_rating) {
      const values = filterValues.financial_risk_rating.split(',').map(v => v.trim()).filter(v => v);
      setFinancialRisk(values);
    } else {
      setFinancialRisk([]);
    }
  }, [filterValues.financial_risk_rating]);

  const handlePolfundCustomerChange = (event) => {
    const value = event.target.value;
    const newFilters = { ...filterValues };
    
    if (value === '' || value === null || value === undefined) {
      delete newFilters.is_current_customer;
    } else {
      newFilters.is_current_customer = value === 'true';
    }
    
    setFilters(newFilters, {});
  };

  const handleConsentEmailChange = (e) => {
    const value = e.target.value;
    setConsentEmail(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.consent_email;
    } else {
      newFilters.consent_email = value === 'true';
    }
    setFilters(newFilters, {});
  };

  const handleConsentPhoneChange = (e) => {
    const value = e.target.value;
    setConsentPhone(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.consent_phone;
    } else {
      newFilters.consent_phone = value === 'true';
    }
    setFilters(newFilters, {});
  };

  const handleConsentGroupCompanyChange = (e) => {
    const value = e.target.value;
    setConsentGroupCompany(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.consent_group_company;
    } else {
      newFilters.consent_group_company = value === 'true';
    }
    setFilters(newFilters, {});
  };

  const handleConsentEmailDateFromChange = (e) => {
    const value = e.target.value;
    setConsentEmailDateFrom(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.consent_email_date_from;
    } else {
      newFilters.consent_email_date_from = value;
    }
    setFilters(newFilters, {});
  };

  const handleConsentEmailDateToChange = (e) => {
    const value = e.target.value;
    setConsentEmailDateTo(value);
    
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.consent_email_date_to;
    } else {
      newFilters.consent_email_date_to = value;
    }
    setFilters(newFilters, {});
  };

  const handleFinancialRiskChange = (event) => {
    const value = event.target.value;
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    setFinancialRisk(selectedValues);
    
    const newFilters = { ...filterValues };
    if (!selectedValues || selectedValues.length === 0) {
      delete newFilters.financial_risk_rating;
    } else {
      newFilters.financial_risk_rating = selectedValues.join(',');
    }
    setFilters(newFilters, {});
  };

  const handleBidderStatusChange = (e) => {
    const value = e.target.value;
    setBidderStatus(value);
    const newFilters = { ...filterValues };
    if (value === '') {
      delete newFilters.status;
    } else {
      newFilters.status = value;
    }
    setFilters(newFilters, {});
  };

  const handleBlockedAtFromChange = (e) => {
    const value = e.target.value;
    setBlockedAtFrom(value);
    const newFilters = { ...filterValues };
    if (value === '') { delete newFilters.blocked_at_from; } else { newFilters.blocked_at_from = value; }
    setFilters(newFilters, {});
  };

  const handleBlockedAtToChange = (e) => {
    const value = e.target.value;
    setBlockedAtTo(value);
    const newFilters = { ...filterValues };
    if (value === '') { delete newFilters.blocked_at_to; } else { newFilters.blocked_at_to = value; }
    setFilters(newFilters, {});
  };

  const handleResetFilters = () => {
    setConsentEmail('');
    setConsentPhone('');
    setConsentGroupCompany('');
    setConsentEmailDateFrom('');
    setConsentEmailDateTo('');
    setBlockedAtFrom('');
    setBlockedAtTo('');
    setFinancialRisk([]);
    setBidderStatus('');
    setFilterKey(Date.now());

    requestAnimationFrame(() => {
      const allFilterFields = {
        name: '',
        tax_number: '',
        registration_number: '',
        krs: '',
        pkd: '',
        pkd_others: '',
        city: '',
        municipality: '',
        province: '',
        is_current_customer: '',
        consent_email: '',
        consent_phone: '',
        consent_group_company: '',
        consent_email_date_from: '',
        consent_email_date_to: '',
        blocked_at_from: '',
        blocked_at_to: '',
        financial_risk_rating: '',
        status: '',
      };

      setFilters(allFilterFields, {});

      requestAnimationFrame(() => {
        setFilters({}, {});
      });
    });
  };

  return (
    <Card sx={{ order: -1, mr: 2, mt: 9, minWidth: 250, maxWidth: 300, boxShadow: 0, borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={1.5}>
          <FilterLiveSearch key={`name-${filterKey}`} source="name" label={translate('resources.bidders.fields.name')} />
          <FilterLiveSearch key={`tax_number-${filterKey}`} source="tax_number" label={translate('resources.bidders.fields.tax_number')} />
          <FilterLiveSearch key={`registration_number-${filterKey}`} source="registration_number" label={translate('resources.bidders.fields.registration_number')} />
          <FilterLiveSearch key={`krs-${filterKey}`} source="krs" label={translate('resources.bidders.fields.krs')} />
          <FilterLiveSearch key={`pkd-${filterKey}`} source="pkd" label={translate('resources.bidders.fields.pkd')} />
          <FilterLiveSearch key={`pkd_others-${filterKey}`} source="pkd_others" label="Pozostałe kody PKD" />
          <FilterLiveSearch key={`city-${filterKey}`} source="city" label={translate('resources.bidders.fields.city')} />
          <FilterLiveSearch key={`municipality-${filterKey}`} source="municipality" label={translate('resources.bidders.fields.municipality')} />
          <FilterLiveSearch key={`province-${filterKey}`} source="province" label={translate('resources.bidders.fields.province')} />
          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="polfund-customer-filter-label" sx={{ fontSize: '0.875rem' }}>Czy klient Polfund</InputLabel>
            <Select
              labelId="polfund-customer-filter-label"
              value={getSelectValue()}
              onChange={handlePolfundCustomerChange}
              label="Czy klient Polfund"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="financial-risk-filter-label" sx={{ fontSize: '0.875rem' }}>
              Skala ryzyka finansowego
            </InputLabel>
            <Select
              labelId="financial-risk-filter-label"
              multiple
              value={financialRisk}
              onChange={handleFinancialRiskChange}
              label="Skala ryzyka finansowego"
              sx={{ fontSize: '0.875rem' }}
              renderValue={(selected) => {
                if (selected.length === 0) return '';
                if (selected.length === 1) {
                  const labels = {
                    '5': '5',
                    '4+': '4+',
                    '4': '4',
                    '4-': '4-',
                    '3+': '3+',
                    '3': '3',
                    '3-': '3-',
                    '2': '2',
                    '?': '?',
                    'none': 'Brak danych'
                  };
                  return labels[selected[0]] || selected[0];
                }
                return `${selected.length} wybrane`;
              }}
            >
              <MenuItem value="5">
                <Checkbox checked={financialRisk.indexOf('5') > -1} />
                <ListItemText primary="5 - Znikome ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4+">
                <Checkbox checked={financialRisk.indexOf('4+') > -1} />
                <ListItemText primary="4+ - Minimalne ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4">
                <Checkbox checked={financialRisk.indexOf('4') > -1} />
                <ListItemText primary="4 - Bardzo niskie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="4-">
                <Checkbox checked={financialRisk.indexOf('4-') > -1} />
                <ListItemText primary="4- - Niskie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3+">
                <Checkbox checked={financialRisk.indexOf('3+') > -1} />
                <ListItemText primary="3+ - Średnie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3">
                <Checkbox checked={financialRisk.indexOf('3') > -1} />
                <ListItemText primary="3 - Wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="3-">
                <Checkbox checked={financialRisk.indexOf('3-') > -1} />
                <ListItemText primary="3- - Bardzo wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="2">
                <Checkbox checked={financialRisk.indexOf('2') > -1} />
                <ListItemText primary="2 - Krytycznie wysokie ryzyko finansowe" />
              </MenuItem>
              <MenuItem value="?">
                <Checkbox checked={financialRisk.indexOf('?') > -1} />
                <ListItemText primary="? - Brak danych do oceny finansowej" />
              </MenuItem>
              <MenuItem value="none">
                <Checkbox checked={financialRisk.indexOf('none') > -1} />
                <ListItemText primary="Brak danych" />
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="bidder-status-filter-label" sx={{ fontSize: '0.875rem' }}>
              Status blokady
            </InputLabel>
            <Select
              labelId="bidder-status-filter-label"
              value={bidderStatus}
              onChange={handleBidderStatusChange}
              label="Status blokady"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value=""><em>Wszystkie</em></MenuItem>
              <MenuItem value="ACTIVE">Niezablokowani</MenuItem>
              <MenuItem value="BLOCKED">Zablokowani</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ fontWeight: 500, fontSize: 14, mt: 2 }}>
            Zgody marketingowe
          </Typography>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="consent-email-filter-label" sx={{ fontSize: '0.875rem' }}>
              Zgoda na email
            </InputLabel>
            <Select
              labelId="consent-email-filter-label"
              value={consentEmail}
              onChange={handleConsentEmailChange}
              label="Zgoda na email"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="consent-phone-filter-label" sx={{ fontSize: '0.875rem' }}>
              Zgoda na telefon
            </InputLabel>
            <Select
              labelId="consent-phone-filter-label"
              value={consentPhone}
              onChange={handleConsentPhoneChange}
              label="Zgoda na telefon"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
            <InputLabel id="consent-group-company-filter-label" sx={{ fontSize: '0.875rem' }}>
              Zgoda na grupę firm
            </InputLabel>
            <Select
              labelId="consent-group-company-filter-label"
              value={consentGroupCompany}
              onChange={handleConsentGroupCompanyChange}
              label="Zgoda na grupę firm"
              sx={{ fontSize: '0.875rem' }}
            >
              <MenuItem value="">
                <em>Wszystkie</em>
              </MenuItem>
              <MenuItem value="true">TAK</MenuItem>
              <MenuItem value="false">NIE</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ fontWeight: 500, fontSize: 14, mt: 2 }}>
            Data zgody email
          </Typography>

          <MuiTextField
            label="Od"
            type="date"
            value={consentEmailDateFrom}
            onChange={handleConsentEmailDateFromChange}
            size="small"
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mt: 0.5 }}
          />

          <MuiTextField
            label="Do"
            type="date"
            value={consentEmailDateTo}
            onChange={handleConsentEmailDateToChange}
            size="small"
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mt: 0.5 }}
          />

          {showBlockedAtFilter && (
            <>
              <Typography sx={{ fontWeight: 500, fontSize: 14, mt: 2 }}>
                Data blokady
              </Typography>
              <MuiTextField
                label="Od"
                type="date"
                value={blockedAtFrom}
                onChange={handleBlockedAtFromChange}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <MuiTextField
                label="Do"
                type="date"
                value={blockedAtTo}
                onChange={handleBlockedAtToChange}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          <MuiButton
            variant="outlined"
            size="small"
            onClick={handleResetFilters}
            sx={{ mt: 3, mb: 1 }}
            fullWidth
          >
            Wyczyść filtry
          </MuiButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

const DeletedBidderFilterSidebar = () => {
  const translate = useTranslate();
  const { setFilters, filterValues } = useListContext();
  
  const [filterKey, setFilterKey] = useState(Date.now());

  useEffect(() => {
    const hasAnyFilter = 
      filterValues.tax_number || 
      filterValues.registration_number ||
      filterValues.krs;
    
    if (!hasAnyFilter && Object.keys(filterValues).length === 0) {
      setFilterKey(Date.now());
    }
  }, [filterValues]);

  const handleResetFilters = () => {
    setFilterKey(Date.now());
    
    requestAnimationFrame(() => {
      const allFilterFields = {
        tax_number: '',
        registration_number: '',
        krs: '',
      };
      
      setFilters(allFilterFields, {});
      
      requestAnimationFrame(() => {
        setFilters({}, {});
      });
    });
  };

  return (
    <Card sx={{ order: -1, mr: 2, mt: 9, minWidth: 250, maxWidth: 300, boxShadow: 0, borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={1.5}>
          <FilterLiveSearch key={`tax_number-${filterKey}`} source="tax_number" label={translate('resources.bidders.fields.tax_number')} />
          <FilterLiveSearch key={`registration_number-${filterKey}`} source="registration_number" label={translate('resources.bidders.fields.registration_number')} />
          <FilterLiveSearch key={`krs-${filterKey}`} source="krs" label={translate('resources.bidders.fields.krs')} />

          <MuiButton 
            variant="outlined" 
            size="small" 
            onClick={handleResetFilters}
            sx={{ mt: 3, mb: 1 }}
            fullWidth
          >
            Wyczyść filtry
          </MuiButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const BidderList = props => {
    const translate = useTranslate();
    return (
        <List
            {...props}
            actions={<ListActions />}
            perPage={25}
            sort={{ field: 'name', order: 'ASC' }}
            aside={<BidderFilterSidebar />}
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ 
                        minWidth: 'auto',
                        '& .column-marketing_consents': { minWidth: '180px', maxWidth: '220px' },
                        '& .column-financial_risk': { minWidth: '120px', maxWidth: '150px' }
                    }}
                    rowClick="show"
                    bulkActionButtons={false}
                    omit={[
                        'external_id',
                        'source',
                        'street',
                        'postal_code',
                        'country',
                        'website',
                        'company_type',
                        'business_start_date',
                        'business_end_date',
                        'business_suspension_date',
                        'business_resumption_date',
                        'total_wins',
                        'total_value',
                        'updated_at',
                        'last_email_sent',
                        'pkd_others',
                        'consent_email',
                        'consent_phone',
                        'consent_group_company',
                        'details',
                        'total_offers',
                        'active_offers',
                        'is_current_customer',
                        'financial_risk'
                    ]}
                >
                    <TextField source="id" label={translate('resources.bidders.fields.id')} />
                    <TextField source="name" label={translate('resources.bidders.fields.name')} />
                    <StatusField source="status" label={translate('resources.bidders.fields.status')} />
                    <TypeField source="type" label={translate('resources.bidders.fields.type')} />
                    <TextField source="external_id" label={translate('resources.bidders.fields.external_id')} />
                    <TextField source="source" label={translate('resources.bidders.fields.source')} />
                    <TextField source="tax_number" label={translate('resources.bidders.fields.tax_number')} />
                    <TextField source="registration_number" label={translate('resources.bidders.fields.registration_number')} />
                    <TextField source="krs" label={translate('resources.bidders.fields.krs')} />
                    <TextField source="pkd" label={translate('resources.bidders.fields.pkd')} />
                    <TextField source="pkd_others" label="Pozostałe kody PKD" />
                    <TextField source="street" label={translate('resources.bidders.fields.street')} />
                    <TextField source="postal_code" label={translate('resources.bidders.fields.postal_code')} />
                    <TextField source="city" label={translate('resources.bidders.fields.city')} />
                    <TextField source="municipality" label={translate('resources.bidders.fields.municipality')} />
                    <TextField source="province" label={translate('resources.bidders.fields.province')} />
                    <TextField source="country" label={translate('resources.bidders.fields.country')} />
                    <EmailField source="email" label={translate('resources.bidders.fields.email')} />
                    <UrlField source="website" label={translate('resources.bidders.fields.website')} />
                    <TextField source="company_type" label={translate('resources.bidders.fields.company_type')} />
                    <DateField source="business_start_date" label={translate('resources.bidders.fields.business_start_date')} />
                    <DateField source="business_end_date" label={translate('resources.bidders.fields.business_end_date')} />
                    <DateField source="business_suspension_date" label={translate('resources.bidders.fields.business_suspension_date')} />
                    <DateField source="business_resumption_date" label={translate('resources.bidders.fields.business_resumption_date')} />
                    <NumberField source="total_wins" label={translate('resources.bidders.fields.total_wins')} />
                    <NumberField source="total_value" label={translate('resources.bidders.fields.total_value')} options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }} />
                    <FunctionField
                        source="financial_risk"
                        label="Ryzyko finansowe"
                        render={() => <BidderFinancialRiskField />}
                    />
                    <DateField source="created_at" label={translate('resources.bidders.fields.created_at')} showTime />
                    <DateField source="updated_at" label={translate('resources.bidders.fields.updated_at')} showTime />
                    <DateField source="last_email_sent" label={translate('resources.bidders.fields.last_email_sent')} showTime />
                    <ShowButton label={translate('ra.action.show')} />
                </DatagridConfigurable>
            </div>
        </List>
    );
};

const BidderMarketingConsents = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const [consents, setConsents] = React.useState(null);
    const [history, setHistory] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const translate = useTranslate();

    React.useEffect(() => {
        const fetchConsents = async () => {
            if (!record?.id) return;
            try {
                setLoading(true);
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const token = localStorage.getItem('access_token');
                const response = await fetch(`${apiUrl}/bidders/${record.id}/consents/`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setConsents(data.current || data);
                    setHistory(data.history || []);
                } else {
                    throw new Error('Failed to fetch consents');
                }
            } catch (error) {
                console.error('Error fetching consents:', error);
                setConsents({
                    email: { status: 'NIE', given: false },
                    phone: { status: 'NIE', given: false },
                    group_company: { status: 'NIE', given: false }
                });
                setHistory([]);
            } finally {
                setLoading(false);
            }
        };
        fetchConsents();
    }, [record?.id]);

    const consentLabels = React.useMemo(() => ({
        email: translate('resources.bidders.consents.email', 'Zgoda marketingowa - Email'),
        phone: translate('resources.bidders.consents.phone', 'Zgoda marketingowa - Telefon'),
        group_company: translate('resources.bidders.consents.group_company', 'Zgoda na firmy z grupy kapitałowej')
    }), [translate]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('pl-PL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return <Box sx={{ p: 2 }}><Typography>Ładowanie...</Typography></Box>;
    }

    return (
        <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
                <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {translate('resources.bidders.sections.basic_info', 'Informacje podstawowe')}
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={1}>
                        <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.id')}</Grid>
                        <Grid item xs={7}><TextField source="id" /></Grid>
                        <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.name')}</Grid>
                        <Grid item xs={7}><TextField source="name" /></Grid>
                        <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.notes')}</Grid>
                        <Grid item xs={7}>
                            <Box sx={{ 
                                backgroundColor: '#f5f5f5', 
                                border: '1px solid #e0e0e0', 
                                borderRadius: 1, 
                                p: 1.5,
                                minHeight: '60px',
                                display: 'flex',
                                alignItems: 'flex-start'
                            }}>
                                <TextField source="notes" sx={{ 
                                    '& .MuiTypography-root': { 
                                        fontWeight: 500,
                                        color: '#1976d2'
                                    }
                                }} />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
            <Grid item xs={12} md={6}>
                <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {translate('resources.bidders.sections.marketing_consents', 'Zgody marketingowe')}
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={1}>
                        {consents && Object.entries(consents).map(([key, consent], index) => (
                            <React.Fragment key={key}>
                                <Grid item xs={5} sx={{ color: 'text.secondary', mb: index < Object.entries(consents).length - 1 ? 1.5 : 0 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {consentLabels[key] || key}
                                    </Typography>
                                </Grid>
                                <Grid item xs={7} sx={{ mb: index < Object.entries(consents).length - 1 ? 1.5 : 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip 
                                            label={consent.status || (consent.given ? 'TAK' : 'NIE')}
                                            color={consent.given ? 'success' : 'default'}
                                            size="small"
                                        />
                                        {consent.given && consent.source && (
                                            <>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                    {consent.source}
                                                </Typography>
                                                {consent.created_at && (
                                                    <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                                        {formatDate(consent.created_at)}
                                                    </Typography>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                            </React.Fragment>
                        ))}
                    </Grid>
                    {history && history.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                Historia zmian
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ 
                                maxHeight: '400px', 
                                overflowY: 'auto',
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                p: 2,
                                backgroundColor: '#fafafa'
                            }}>
                                {history.map((item) => (
                                    <Box key={item.id} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid #e0e0e0', '&:last-child': { borderBottom: 'none', mb: 0, pb: 0 } }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            <strong>{formatDate(item.created_at)}</strong>{' '}
                                            <span style={{ 
                                                color: item.action === 'Nadano' ? '#2e7d32' : '#d32f2f',
                                                fontWeight: 500
                                            }}>
                                                {item.action} zgodę
                                            </span>
                                            : <strong>{item.consent_type_label}</strong>
                                            {item.source && ` ${item.source}`}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};

const BidderGeoPanelWrapper = () => {
    const record = useRecordContext();
    if (!record) return null;
    return <BidderGeoPanel record={record} />;
};

export const BidderShow = props => {
    const translate = useTranslate();
    return (
        <Show {...props} actions={<BidderShowActions />}>
            <CustomerStatusAlert />
            <TabbedShowLayout>
                <Tab label={translate('resources.bidders.tabs.basic_info')}>
                    <Grid container spacing={4}>
                        {/* Basic Info Column */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {translate('resources.bidders.sections.basic_info', 'Informacje podstawowe')}
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Grid container spacing={1}>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.id')}</Grid>
                                    <Grid item xs={7}><TextField source="id" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.name')}</Grid>
                                    <Grid item xs={7}><TextField source="name" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.status')}</Grid>
                                    <Grid item xs={7}><StatusField source="status" /></Grid>
                                    <BlockedInfo />
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>Czy klient Polfund</Grid>
                                    <Grid item xs={7}>
                                        <FunctionField 
                                            render={record => 
                                                record.is_current_customer ? 
                                                    <Chip label="TAK" color="error" size="small" /> : 
                                                    <Chip label="NIE" color="default" size="small" />
                                            } 
                                        />
                                    </Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.external_id')}</Grid>
                                    <Grid item xs={7}><TextField source="external_id" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.source')}</Grid>
                                    <Grid item xs={7}><TextField source="source" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.tax_number')}</Grid>
                                    <Grid item xs={7}><TextField source="tax_number" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.registration_number')}</Grid>
                                    <Grid item xs={7}><TextField source="registration_number" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.krs')}</Grid>
                                    <Grid item xs={7}><TextField source="krs" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.pkd')}</Grid>
                                    <Grid item xs={7}><TextField source="pkd" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>Pozostałe kody PKD</Grid>
                                    <Grid item xs={7}><PkdOthersField source="pkd_others" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.type')}</Grid>
                                    <Grid item xs={7}><TypeField source="type" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.notes')}</Grid>
                                    <Grid item xs={7}>
                                        <Box sx={{ 
                                            backgroundColor: '#f5f5f5', 
                                            border: '1px solid #e0e0e0', 
                                            borderRadius: 1, 
                                            p: 1.5,
                                            minHeight: '60px',
                                            display: 'flex',
                                            alignItems: 'flex-start'
                                        }}>
                                            <TextField source="notes" sx={{ 
                                                '& .MuiTypography-root': { 
                                                    fontWeight: 500,
                                                    color: '#1976d2'
                                                }
                                            }} />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>
                        {/* Address & Contact Column */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {translate('resources.bidders.sections.address', 'Adres i kontakt')}
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Grid container spacing={1}>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.street')}</Grid>
                                    <Grid item xs={7}><TextField source="street" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.postal_code')}</Grid>
                                    <Grid item xs={7}><TextField source="postal_code" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.city')}</Grid>
                                    <Grid item xs={7}><TextField source="city" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.municipality')}</Grid>
                                    <Grid item xs={7}><TextField source="municipality" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.province')}</Grid>
                                    <Grid item xs={7}><TextField source="province" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.country')}</Grid>
                                    <Grid item xs={7}><TextField source="country" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.email')}</Grid>
                                    <Grid item xs={7}><EmailField source="email" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.phone')}</Grid>
                                    <Grid item xs={7}><TextField source="phone" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.website')}</Grid>
                                    <Grid item xs={7}><UrlField source="website" /></Grid>
                                </Grid>
                                <Suspense fallback={null}>
                                    <BidderGeoPanelWrapper />
                                </Suspense>
                            </Box>
                        </Grid>
                        {/* Company Info & Stats - full width */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {translate('resources.bidders.sections.company_info', 'Informacje o firmie')}
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Grid container spacing={1}>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.company_type')}</Grid>
                                    <Grid item xs={7}><TextField source="company_type" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.company_type_name')}</Grid>
                                    <Grid item xs={7}><TextField source="company_type_name" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.business_start_date')}</Grid>
                                    <Grid item xs={7}><DateField source="business_start_date" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.business_end_date')}</Grid>
                                    <Grid item xs={7}><DateField source="business_end_date" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.business_suspension_date')}</Grid>
                                    <Grid item xs={7}><DateField source="business_suspension_date" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.business_resumption_date')}</Grid>
                                    <Grid item xs={7}><DateField source="business_resumption_date" /></Grid>
                                </Grid>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {translate('resources.bidders.sections.stats', 'Statystyki')}
                                </Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Grid container spacing={1}>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.total_offers')}</Grid>
                                    <Grid item xs={7}><NumberField source="total_offers" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.active_offers')}</Grid>
                                    <Grid item xs={7}><NumberField source="active_offers" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.total_wins')}</Grid>
                                    <Grid item xs={7}><NumberField source="total_wins" /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.total_value')}</Grid>
                                    <Grid item xs={7}><NumberField source="total_value" options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }} /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.created_at')}</Grid>
                                    <Grid item xs={7}><DateField source="created_at" showTime /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.updated_at')}</Grid>
                                    <Grid item xs={7}><DateField source="updated_at" showTime /></Grid>
                                    <Grid item xs={5} sx={{ color: 'text.secondary' }}>{translate('resources.bidders.fields.last_email_sent')}</Grid>
                                    <Grid item xs={7}><DateField source="last_email_sent" showTime /></Grid>
                                </Grid>
                            </Box>
                        </Grid>
                    </Grid>
                </Tab>
                <Tab label={translate('resources.bidders.tabs.pwg_details')}>
                    <BidderPWGDetails />
                </Tab>
                <Tab label={translate('resources.bidders.tabs.checks')}>
                    <BidderChecksList />
                </Tab>
                <Tab label={translate('resources.bidders.tabs.tenders')}>
                    <BidderTendersList />
                </Tab>
                <Tab label={translate('resources.bidders.tabs.emails', 'Emails')}>
                    <BidderEmailsList />
                </Tab>
                <Tab label={translate('resources.bidders.tabs.marketing_consent', 'Zgoda marketingowa')}>
                    <BidderMarketingConsents />
                </Tab>
            </TabbedShowLayout>
        </Show>
    );
};

const BidderEditToolbar = ({ onTransform, onMutationSuccess, ...props }) => {
    const record = useRecordContext();
    const { getValues } = useFormContext();
    const [update, { isLoading }] = useUpdate();
    const notify = useNotify();
    const redirect = useRedirect();
    
    const handleSave = useCallback(async () => {
        const values = getValues();
        const transformedValues = onTransform ? onTransform(values) : values;
        
        try {
            await update(
                'bidders',
                { id: record.id, data: transformedValues },
                {
                    onSuccess: (data) => {
                        if (onMutationSuccess) {
                            onMutationSuccess(data);
                        }
                        notify('Element zaktualizowany', { type: 'success' });
                        redirect('show', 'bidders', record.id);
                    },
                    onError: (error) => {
                        notify(error.message || 'Błąd podczas zapisywania', { type: 'error' });
                    }
                }
            );
        } catch (error) {
            notify('Błąd podczas zapisywania', { type: 'error' });
        }
    }, [record, getValues, update, notify, redirect, onTransform, onMutationSuccess]);
    
    return (
        <Toolbar {...props}>
            <SaveButton onClick={handleSave} disabled={isLoading} />
        </Toolbar>
    );
};

const BidderConsentEditFields = ({ onConsentsLoaded, onConsentValuesChange }) => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { setValue, control } = useFormContext();
    const [loading, setLoading] = React.useState(true);
    const [initialized, setInitialized] = React.useState(false);

    const consentFieldNames = React.useMemo(
        () => CONSENT_TYPES.map(type => CONSENT_FIELD_MAP[type]),
        []
    );

    React.useEffect(() => {
        let isActive = true;
        const fetchConsents = async () => {
            if (!record?.id) return;
            try {
                setLoading(true);
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
                const token = localStorage.getItem('access_token');
                const response = await fetch(`${apiUrl}/bidders/${record.id}/consents/`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                    },
                });
                let normalized = normalizeConsents({});
                if (response.ok) {
                    const data = await response.json();
                    normalized = normalizeConsents(data.current || data);
                }
                if (isActive) {
                    CONSENT_TYPES.forEach(type => {
                        const field = CONSENT_FIELD_MAP[type];
                        setValue(field, normalized[type], { shouldDirty: false });
                    });
                    onConsentsLoaded?.(normalized);
                    onConsentValuesChange?.(normalized);
                    setInitialized(true);
                }
            } catch (error) {
                console.error('Error fetching consents:', error);
            } finally {
                if (isActive) {
                    setLoading(false);
                }
            }
        };
        fetchConsents();
        return () => {
            isActive = false;
        };
    }, [record?.id, setValue, onConsentsLoaded, onConsentValuesChange]);

    const watchedValues = useWatch({
        control,
        name: consentFieldNames
    });

    React.useEffect(() => {
        if (!initialized || !watchedValues) {
            return;
        }
        const current = {};
        consentFieldNames.forEach((field, index) => {
            current[CONSENT_TYPES[index]] = Boolean(watchedValues[index]);
        });
        onConsentValuesChange?.(current);
    }, [watchedValues, initialized, consentFieldNames, onConsentValuesChange]);

    const consentLabels = React.useMemo(() => ({
        email: translate('resources.bidders.consents.email', 'Zgoda marketingowa - Email'),
        phone: translate('resources.bidders.consents.phone', 'Zgoda marketingowa - Telefon'),
        group_company: translate('resources.bidders.consents.group_company', 'Zgoda na firmy z grupy kapitałowej')
    }), [translate]);

    const booleanChoices = React.useMemo(() => ([
        { id: true, name: 'TAK' },
        { id: false, name: 'NIE' }
    ]), []);

    if (loading && !initialized) {
        return (
            <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 2 }}>
                Ładowanie zgód marketingowych...
            </Typography>
        );
    }

    return (
        <>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 2 }}>
                Zgody marketingowe
            </Typography>
            <SelectInput 
                source="consent_email" 
                label={consentLabels.email}
                choices={booleanChoices}
                fullWidth 
            />
            <SelectInput 
                source="consent_phone" 
                label={consentLabels.phone}
                choices={booleanChoices}
                fullWidth 
            />
            <SelectInput 
                source="consent_group_company" 
                label={consentLabels.group_company}
                choices={booleanChoices}
                fullWidth 
            />
        </>
    );
};

export const BidderEdit = props => {
    const translate = useTranslate();
    const notify = useNotify();
    const initialConsentsRef = useRef(null);
    const currentConsentsRef = useRef(null);
    const submittedConsentsRef = useRef(null);

    const handleConsentsLoaded = useCallback((values) => {
        initialConsentsRef.current = values;
        currentConsentsRef.current = values;
    }, []);

    const handleConsentValuesChange = useCallback((values) => {
        currentConsentsRef.current = values;
    }, []);

    const handleTransform = useCallback((data) => {
        const consentValues = extractConsentValues(data);
        if (Object.keys(consentValues).length) {
            submittedConsentsRef.current = consentValues;
        }
        return stripConsentFields(data);
    }, []);

    const handleMutationSuccess = useCallback(async (data) => {
        if (!data?.id) {
            return;
        }
        const initial = initialConsentsRef.current;
        const submitted = submittedConsentsRef.current || currentConsentsRef.current;
        if (!initial || !submitted) {
            return;
        }

        const changes = diffConsents(initial, submitted);
        if (!changes.length) {
            return;
        }

        try {
            await Promise.all(
                changes.map(change => postConsentUpdate(data.id, change.consent_type, change.given))
            );
            initialConsentsRef.current = { ...submitted };
            submittedConsentsRef.current = null;
            notify('Zgody marketingowe zostały zapisane', { type: 'success' });
        } catch (error) {
            console.error('Error while updating marketing consents', error);
            notify(error.message || 'Nie udało się zapisać zgód marketingowych', { type: 'warning' });
        }
    }, [notify]);

    return (
        <Edit {...props} redirect="show" transform={handleTransform} mutationOptions={{ onSuccess: handleMutationSuccess }}>
            <StyledSimpleForm toolbar={<BidderEditToolbar onTransform={handleTransform} onMutationSuccess={handleMutationSuccess} />}>
                <TextInput source="name" label={translate('resources.bidders.fields.name')} fullWidth />
                <SelectInput 
                    source="status" 
                    label={translate('resources.bidders.fields.status')} 
                    choices={[
                        { id: 'ACTIVE', name: translate('resources.bidders.fields.status_active', 'Active') },
                        { id: 'BLOCKED', name: translate('resources.bidders.fields.status_blocked', 'Blocked') },
                        { id: 'DELETED', name: translate('resources.bidders.fields.status_deleted', 'Deleted') }
                    ]}
                    fullWidth 
                />
                <SelectInput 
                    source="is_current_customer" 
                    label={translate('resources.bidders.fields.is_current_customer', 'Czy klient Polfund')} 
                    choices={[
                        { id: true, name: translate('ra.boolean.true', 'Tak') },
                        { id: false, name: translate('ra.boolean.false', 'Nie') }
                    ]}
                    fullWidth 
                />
                <SelectInput 
                    source="type" 
                    label={translate('resources.bidders.fields.type')} 
                    choices={[
                        { id: 'niski', name: translate('resources.bidders.fields.type_niski', 'Niski') },
                        { id: 'średni', name: translate('resources.bidders.fields.type_średni', 'Średni') },
                        { id: 'wysoki', name: translate('resources.bidders.fields.type_wysoki', 'Wysoki') }
                    ]}
                    fullWidth 
                />
                <TextInput source="tax_number" label={translate('resources.bidders.fields.tax_number')} fullWidth />
                <TextInput source="registration_number" label={translate('resources.bidders.fields.registration_number')} fullWidth />
                <TextInput source="krs" label={translate('resources.bidders.fields.krs')} fullWidth />
                <TextInput source="pkd" label={translate('resources.bidders.fields.pkd')} fullWidth />
                <TextInput source="pkd_others" label="Pozostałe kody PKD" fullWidth />
                <TextInput source="street" label={translate('resources.bidders.fields.street')} fullWidth />
                <TextInput source="postal_code" label={translate('resources.bidders.fields.postal_code')} fullWidth />
                <TextInput source="city" label={translate('resources.bidders.fields.city')} fullWidth />
                <TextInput source="municipality" label={translate('resources.bidders.fields.municipality')} fullWidth />
                <TextInput source="province" label={translate('resources.bidders.fields.province')} fullWidth />
                <TextInput source="country" label={translate('resources.bidders.fields.country')} fullWidth defaultValue="Polska" />
                <TextInput source="email" type="email" label={translate('resources.bidders.fields.email')} fullWidth />
                <TextInput source="phone" label={translate('resources.bidders.fields.phone')} fullWidth />
                <TextInput source="website" label={translate('resources.bidders.fields.website')} fullWidth />
                <TextInput source="company_type" label={translate('resources.bidders.fields.company_type')} fullWidth />
                <TextInput source="company_type_name" label={translate('resources.bidders.fields.company_type_name')} fullWidth />
                <TextInput source="notes" label={translate('resources.bidders.fields.notes')} multiline rows={4} fullWidth sx={{
                    '& .MuiInputBase-root': {
                        backgroundColor: '#f8f9fa',
                        border: '2px solid #e3f2fd',
                        borderRadius: 1,
                        '&:hover': {
                            borderColor: '#1976d2'
                        },
                        '&.Mui-focused': {
                            borderColor: '#1976d2',
                            backgroundColor: '#fff'
                        }
                    },
                    '& .MuiInputLabel-root': {
                        fontWeight: 600,
                        color: '#1976d2'
                    }
                }} />
                <DateInput source="business_start_date" label={translate('resources.bidders.fields.business_start_date')} fullWidth />
                <DateInput source="business_end_date" label={translate('resources.bidders.fields.business_end_date')} fullWidth />
                <DateInput source="business_suspension_date" label={translate('resources.bidders.fields.business_suspension_date')} fullWidth />
                <DateInput source="business_resumption_date" label={translate('resources.bidders.fields.business_resumption_date')} fullWidth />
                <BidderConsentEditFields
                    onConsentsLoaded={handleConsentsLoaded}
                    onConsentValuesChange={handleConsentValuesChange}
                />
            </StyledSimpleForm>
        </Edit>
    );
};

export const BidderCreate = props => {
    const translate = useTranslate();
    return (
        <Create {...props}>
            <StyledSimpleForm>
                <TextInput source="name" label={translate('resources.bidders.fields.name')} validate={required()} fullWidth />
                <SelectInput 
                    source="status" 
                    label={translate('resources.bidders.fields.status')} 
                    choices={[
                        { id: 'ACTIVE', name: translate('resources.bidders.fields.status_active', 'Active') },
                        { id: 'BLOCKED', name: translate('resources.bidders.fields.status_blocked', 'Blocked') },
                        { id: 'DELETED', name: translate('resources.bidders.fields.status_deleted', 'Deleted') }
                    ]}
                    defaultValue="ACTIVE"
                    fullWidth 
                />
                <SelectInput 
                    source="is_current_customer" 
                    label={translate('resources.bidders.fields.is_current_customer', 'Czy klient Polfund')} 
                    choices={[
                        { id: true, name: translate('ra.boolean.true', 'Tak') },
                        { id: false, name: translate('ra.boolean.false', 'Nie') }
                    ]}
                    fullWidth 
                />
                <SelectInput 
                    source="type" 
                    label={translate('resources.bidders.fields.type')} 
                    choices={[
                        { id: 'niski', name: translate('resources.bidders.fields.type_niski', 'Niski') },
                        { id: 'średni', name: translate('resources.bidders.fields.type_średni', 'Średni') },
                        { id: 'wysoki', name: translate('resources.bidders.fields.type_wysoki', 'Wysoki') }
                    ]}
                    fullWidth 
                />
                <TextInput source="tax_number" label={translate('resources.bidders.fields.tax_number')} fullWidth />
                <TextInput source="registration_number" label={translate('resources.bidders.fields.registration_number')} fullWidth />
                <TextInput source="krs" label={translate('resources.bidders.fields.krs')} fullWidth />
                <TextInput source="pkd" label={translate('resources.bidders.fields.pkd')} fullWidth />
                <TextInput source="pkd_others" label="Pozostałe kody PKD" fullWidth />
                <TextInput source="street" label={translate('resources.bidders.fields.street')} fullWidth />
                <TextInput source="postal_code" label={translate('resources.bidders.fields.postal_code')} fullWidth />
                <TextInput source="city" label={translate('resources.bidders.fields.city')} fullWidth />
                <TextInput source="municipality" label={translate('resources.bidders.fields.municipality')} fullWidth />
                <TextInput source="province" label={translate('resources.bidders.fields.province')} fullWidth />
                <TextInput source="country" label={translate('resources.bidders.fields.country')} fullWidth defaultValue="Polska" />
                <TextInput source="email" type="email" label={translate('resources.bidders.fields.email')} fullWidth />
                <TextInput source="phone" label={translate('resources.bidders.fields.phone')} fullWidth />
                <TextInput source="website" label={translate('resources.bidders.fields.website')} fullWidth />
                <TextInput source="company_type" label={translate('resources.bidders.fields.company_type')} fullWidth />
                <TextInput source="company_type_name" label={translate('resources.bidders.fields.company_type_name')} fullWidth />
                <TextInput source="notes" label={translate('resources.bidders.fields.notes')} multiline rows={4} fullWidth sx={{
                    '& .MuiInputBase-root': {
                        backgroundColor: '#f8f9fa',
                        border: '2px solid #e3f2fd',
                        borderRadius: 1,
                        '&:hover': {
                            borderColor: '#1976d2'
                        },
                        '&.Mui-focused': {
                            borderColor: '#1976d2',
                            backgroundColor: '#fff'
                        }
                    },
                    '& .MuiInputLabel-root': {
                        fontWeight: 600,
                        color: '#1976d2'
                    }
                }} />
                <DateInput source="business_start_date" label={translate('resources.bidders.fields.business_start_date')} fullWidth />
                <DateInput source="business_end_date" label={translate('resources.bidders.fields.business_end_date')} fullWidth />
                <DateInput source="business_suspension_date" label={translate('resources.bidders.fields.business_suspension_date')} fullWidth />
                <DateInput source="business_resumption_date" label={translate('resources.bidders.fields.business_resumption_date')} fullWidth />
            </StyledSimpleForm>
        </Create>
    );
};

// Bidder Checks resource
export const BidderChecksShow = () => (
    <Show>
        <BidderCheckDetails />
    </Show>
);

const bidderChecksResource = {
    name: 'bidder-checks',
    show: BidderChecksShow,
};

const bidderResource = {
    list: BidderList,
    show: BidderShow,
    edit: BidderEdit,
    create: BidderCreate,
    recordRepresentation: 'name',
};

export default {
    ...bidderResource,
    hasShow: true,
    hasList: true,
    hasEdit: true,
    hasCreate: true,
}; 

// Filtered Bidder List Components
export const AllBidderList = props => {
    const translate = useTranslate();
    return (
        <List
            {...props}
            actions={<ListActions />}
            perPage={25}
            sort={{ field: 'created_at', order: 'DESC' }}
            aside={<BidderFilterSidebar />}
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ 
                        minWidth: 'auto',
                        '& .column-marketing_consents': {
                            minWidth: '200px',
                            maxWidth: '250px'
                        },
                        '& .column-financial_risk': { minWidth: '120px', maxWidth: '150px' }
                    }}
                    rowClick="show"
                    bulkActionButtons={false}
                    omit={[
                        'external_id',
                        'source',
                        'street',
                        'postal_code',
                        'country',
                        'email',
                        'website',
                        'company_type',
                        'business_start_date',
                        'business_end_date',
                        'business_suspension_date',
                        'business_resumption_date',
                        'updated_at',
                        'last_email_sent',
                        'pkd_others',
                        'consent_email',
                        'consent_phone',
                        'consent_group_company',
                        'details',
                        'total_offers',
                        'active_offers',
                        'is_current_customer',
                        'financial_risk'
                    ]}
                >
                    <TextField source="id" label={translate('resources.bidders.fields.id')} />
                    <TextField source="name" label={translate('resources.bidders.fields.name')} />
                    <StatusField source="status" label={translate('resources.bidders.fields.status')} />
                    <TypeField source="type" label={translate('resources.bidders.fields.type')} />
                    <NumberField source="total_offers" label={translate('resources.bidders.fields.total_offers')} />
                    <NumberField source="active_offers" label={translate('resources.bidders.fields.active_offers')} />
                    <NumberField source="total_wins" label={translate('resources.bidders.fields.total_wins')} />
                    <NumberField source="total_value" label={translate('resources.bidders.fields.total_value')} options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }} />
                    <TextField source="external_id" label={translate('resources.bidders.fields.external_id')} />
                    <TextField source="source" label={translate('resources.bidders.fields.source')} />
                    <TextField source="tax_number" label={translate('resources.bidders.fields.tax_number')} />
                    <TextField source="registration_number" label={translate('resources.bidders.fields.registration_number')} />
                    <TextField source="krs" label={translate('resources.bidders.fields.krs')} />
                    <TextField source="pkd" label={translate('resources.bidders.fields.pkd')} />
                    <TextField source="pkd_others" label="Pozostałe kody PKD" />
                    <TextField source="street" label={translate('resources.bidders.fields.street')} />
                    <TextField source="postal_code" label={translate('resources.bidders.fields.postal_code')} />
                    <TextField source="city" label={translate('resources.bidders.fields.city')} />
                    <TextField source="municipality" label={translate('resources.bidders.fields.municipality')} />
                    <TextField source="province" label={translate('resources.bidders.fields.province')} />
                    <TextField source="country" label={translate('resources.bidders.fields.country')} />
                    <EmailField source="email" label={translate('resources.bidders.fields.email')} />
                    <UrlField source="website" label={translate('resources.bidders.fields.website')} />
                    <TextField source="company_type" label={translate('resources.bidders.fields.company_type')} />
                    <DateField source="business_start_date" label={translate('resources.bidders.fields.business_start_date')} />
                    <DateField source="business_end_date" label={translate('resources.bidders.fields.business_end_date')} />
                    <DateField source="business_suspension_date" label={translate('resources.bidders.fields.business_suspension_date')} />
                    <DateField source="business_resumption_date" label={translate('resources.bidders.fields.business_resumption_date')} />
                    <FunctionField
                        source="financial_risk"
                        label="Ryzyko finansowe"
                        render={() => <BidderFinancialRiskField />}
                    />
                    <DateField source="created_at" label={translate('resources.bidders.fields.created_at')} showTime />
                    <DateField source="updated_at" label={translate('resources.bidders.fields.updated_at')} showTime />
                    <DateField source="last_email_sent" label={translate('resources.bidders.fields.last_email_sent')} showTime />
                    <DateField source="consent_email_date" label="Data zgody email" showTime />
                    <DateField source="consent_email_withdrawn_at" label="Data wycofania zgody" showTime />
                    <FunctionField 
                        source="id" 
                        label="Zgody marketingowe" 
                        render={(record) => <MarketingConsentsField />}
                        sortable={false}
                    />
                    <ShowButton label={translate('ra.action.show')} />
                </DatagridConfigurable>
            </div>
        </List>
    );
};

export const ArchivedBidderList = props => {
    const translate = useTranslate();
    return (
        <List
            {...props}
            actions={<ListActions />}
            perPage={25}
            sort={{ field: 'name', order: 'ASC' }}
            aside={<BidderFilterSidebar />}
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ 
                        minWidth: 'auto',
                        '& .column-financial_risk': { minWidth: '120px', maxWidth: '150px' }
                    }}
                    rowClick="show"
                    bulkActionButtons={false}
                    omit={[
                        'external_id',
                        'source',
                        'street',
                        'postal_code',
                        'country',
                        'email',
                        'website',
                        'company_type',
                        'business_start_date',
                        'business_end_date',
                        'business_suspension_date',
                        'business_resumption_date',
                        'total_wins',
                        'total_value',
                        'updated_at',
                        'last_email_sent',
                        'pkd_others',
                        'financial_risk'
                    ]}
                >
                    <TextField source="id" label={translate('resources.bidders.fields.id')} />
                    <TextField source="name" label={translate('resources.bidders.fields.name')} />
                    <StatusField source="status" label={translate('resources.bidders.fields.status')} />
                    <TypeField source="type" label={translate('resources.bidders.fields.type')} />
                    <TextField source="external_id" label={translate('resources.bidders.fields.external_id')} />
                    <TextField source="source" label={translate('resources.bidders.fields.source')} />
                    <TextField source="tax_number" label={translate('resources.bidders.fields.tax_number')} />
                    <TextField source="registration_number" label={translate('resources.bidders.fields.registration_number')} />
                    <TextField source="krs" label={translate('resources.bidders.fields.krs')} />
                    <TextField source="pkd" label={translate('resources.bidders.fields.pkd')} />
                    <TextField source="pkd_others" label="Pozostałe kody PKD" />
                    <TextField source="street" label={translate('resources.bidders.fields.street')} />
                    <TextField source="postal_code" label={translate('resources.bidders.fields.postal_code')} />
                    <TextField source="city" label={translate('resources.bidders.fields.city')} />
                    <TextField source="municipality" label={translate('resources.bidders.fields.municipality')} />
                    <TextField source="province" label={translate('resources.bidders.fields.province')} />
                    <TextField source="country" label={translate('resources.bidders.fields.country')} />
                    <EmailField source="email" label={translate('resources.bidders.fields.email')} />
                    <UrlField source="website" label={translate('resources.bidders.fields.website')} />
                    <TextField source="company_type" label={translate('resources.bidders.fields.company_type')} />
                    <DateField source="business_start_date" label={translate('resources.bidders.fields.business_start_date')} />
                    <DateField source="business_end_date" label={translate('resources.bidders.fields.business_end_date')} />
                    <DateField source="business_suspension_date" label={translate('resources.bidders.fields.business_suspension_date')} />
                    <DateField source="business_resumption_date" label={translate('resources.bidders.fields.business_resumption_date')} />
                    <NumberField source="total_wins" label={translate('resources.bidders.fields.total_wins')} />
                    <NumberField source="total_value" label={translate('resources.bidders.fields.total_value')} options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }} />
                    <FunctionField
                        source="financial_risk"
                        label="Ryzyko finansowe"
                        render={() => <BidderFinancialRiskField />}
                    />
                    <DateField source="created_at" label={translate('resources.bidders.fields.created_at')} showTime />
                    <DateField source="updated_at" label={translate('resources.bidders.fields.updated_at')} showTime />
                    <DateField source="last_email_sent" label={translate('resources.bidders.fields.last_email_sent')} showTime />
                    <ShowButton label={translate('ra.action.show')} />
                </DatagridConfigurable>
            </div>
        </List>
    );
};

export const DeletedBidderList = props => {
    const translate = useTranslate();
    return (
        <List
            {...props}
            actions={<ListActions />}
            perPage={25}
            sort={{ field: 'name', order: 'ASC' }}
            aside={<DeletedBidderFilterSidebar />}
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ 
                        minWidth: 'auto',
                        '& .column-financial_risk': { minWidth: '120px', maxWidth: '150px' }
                    }}
                    rowClick="show"
                    bulkActionButtons={false}
                    omit={[
                        'external_id',
                        'source',
                        'street',
                        'postal_code',
                        'country',
                        'email',
                        'website',
                        'company_type',
                        'business_start_date',
                        'business_end_date',
                        'business_suspension_date',
                        'business_resumption_date',
                        'total_wins',
                        'total_value',
                        'updated_at',
                        'last_email_sent',
                        'pkd_others',
                        'financial_risk'
                    ]}
                >
                    <TextField source="id" label={translate('resources.bidders.fields.id')} />
                    <TextField source="name" label={translate('resources.bidders.fields.name')} />
                    <StatusField source="status" label={translate('resources.bidders.fields.status')} />
                    <TypeField source="type" label={translate('resources.bidders.fields.type')} />
                    <TextField source="external_id" label={translate('resources.bidders.fields.external_id')} />
                    <TextField source="source" label={translate('resources.bidders.fields.source')} />
                    <TextField source="tax_number" label={translate('resources.bidders.fields.tax_number')} />
                    <TextField source="registration_number" label={translate('resources.bidders.fields.registration_number')} />
                    <TextField source="krs" label={translate('resources.bidders.fields.krs')} />
                    <TextField source="pkd" label={translate('resources.bidders.fields.pkd')} />
                    <TextField source="pkd_others" label="Pozostałe kody PKD" />
                    <TextField source="street" label={translate('resources.bidders.fields.street')} />
                    <TextField source="postal_code" label={translate('resources.bidders.fields.postal_code')} />
                    <TextField source="city" label={translate('resources.bidders.fields.city')} />
                    <TextField source="municipality" label={translate('resources.bidders.fields.municipality')} />
                    <TextField source="province" label={translate('resources.bidders.fields.province')} />
                    <TextField source="country" label={translate('resources.bidders.fields.country')} />
                    <EmailField source="email" label={translate('resources.bidders.fields.email')} />
                    <UrlField source="website" label={translate('resources.bidders.fields.website')} />
                    <TextField source="company_type" label={translate('resources.bidders.fields.company_type')} />
                    <DateField source="business_start_date" label={translate('resources.bidders.fields.business_start_date')} />
                    <DateField source="business_end_date" label={translate('resources.bidders.fields.business_end_date')} />
                    <DateField source="business_suspension_date" label={translate('resources.bidders.fields.business_suspension_date')} />
                    <DateField source="business_resumption_date" label={translate('resources.bidders.fields.business_resumption_date')} />
                    <NumberField source="total_wins" label={translate('resources.bidders.fields.total_wins')} />
                    <NumberField source="total_value" label={translate('resources.bidders.fields.total_value')} options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }} />
                    <FunctionField
                        source="financial_risk"
                        label="Ryzyko finansowe"
                        render={() => <BidderFinancialRiskField />}
                    />
                    <DateField source="created_at" label={translate('resources.bidders.fields.created_at')} showTime />
                    <DateField source="updated_at" label={translate('resources.bidders.fields.updated_at')} showTime />
                    <DateField source="last_email_sent" label={translate('resources.bidders.fields.last_email_sent')} showTime />
                    <ShowButton label={translate('ra.action.show')} />
                </DatagridConfigurable>
            </div>
        </List>
    );
};

export const BlockedBidderList = props => {
    const translate = useTranslate();
    return (
        <List
            {...props}
            actions={<ListActions fixedFilters={{ status: 'BLOCKED' }} />}
            perPage={25}
            sort={{ field: 'blocked_at', order: 'DESC' }}
            filter={{ status: 'BLOCKED' }}
            aside={<BidderFilterSidebar showBlockedAtFilter />}
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ 
                        minWidth: 'auto',
                        '& .column-financial_risk': { minWidth: '120px', maxWidth: '150px' }
                    }}
                    rowClick="show"
                    bulkActionButtons={false}
                    omit={[
                        'external_id',
                        'source',
                        'street',
                        'postal_code',
                        'country',
                        'email',
                        'website',
                        'company_type',
                        'business_start_date',
                        'business_end_date',
                        'business_suspension_date',
                        'business_resumption_date',
                        'total_wins',
                        'total_value',
                        'updated_at',
                        'last_email_sent',
                        'pkd_others',
                        'financial_risk'
                    ]}
                >
                    <TextField source="id" label={translate('resources.bidders.fields.id')} />
                    <TextField source="name" label={translate('resources.bidders.fields.name')} />
                    <StatusField source="status" label={translate('resources.bidders.fields.status')} />
                    <DateField source="blocked_at" label="Data blokady" showTime />
                    <FunctionField source="blocked_by_name" label="Zablokowany przez" sortBy="blocked_by_name" render={record => record.blocked_by_name || '—'} />
                    <TypeField source="type" label={translate('resources.bidders.fields.type')} />
                    <TextField source="external_id" label={translate('resources.bidders.fields.external_id')} />
                    <TextField source="source" label={translate('resources.bidders.fields.source')} />
                    <TextField source="tax_number" label={translate('resources.bidders.fields.tax_number')} />
                    <TextField source="registration_number" label={translate('resources.bidders.fields.registration_number')} />
                    <TextField source="krs" label={translate('resources.bidders.fields.krs')} />
                    <TextField source="pkd" label={translate('resources.bidders.fields.pkd')} />
                    <TextField source="pkd_others" label="Pozostałe kody PKD" />
                    <TextField source="street" label={translate('resources.bidders.fields.street')} />
                    <TextField source="postal_code" label={translate('resources.bidders.fields.postal_code')} />
                    <TextField source="city" label={translate('resources.bidders.fields.city')} />
                    <TextField source="municipality" label={translate('resources.bidders.fields.municipality')} />
                    <TextField source="province" label={translate('resources.bidders.fields.province')} />
                    <TextField source="country" label={translate('resources.bidders.fields.country')} />
                    <EmailField source="email" label={translate('resources.bidders.fields.email')} />
                    <UrlField source="website" label={translate('resources.bidders.fields.website')} />
                    <TextField source="company_type" label={translate('resources.bidders.fields.company_type')} />
                    <DateField source="business_start_date" label={translate('resources.bidders.fields.business_start_date')} />
                    <DateField source="business_end_date" label={translate('resources.bidders.fields.business_end_date')} />
                    <DateField source="business_suspension_date" label={translate('resources.bidders.fields.business_suspension_date')} />
                    <DateField source="business_resumption_date" label={translate('resources.bidders.fields.business_resumption_date')} />
                    <NumberField source="total_wins" label={translate('resources.bidders.fields.total_wins')} />
                    <NumberField source="total_value" label={translate('resources.bidders.fields.total_value')} options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }} />
                    <FunctionField
                        source="financial_risk"
                        label="Ryzyko finansowe"
                        render={() => <BidderFinancialRiskField />}
                    />
                    <DateField source="created_at" label={translate('resources.bidders.fields.created_at')} showTime />
                    <DateField source="updated_at" label={translate('resources.bidders.fields.updated_at')} showTime />
                    <DateField source="last_email_sent" label={translate('resources.bidders.fields.last_email_sent')} showTime />
                    <ShowButton label={translate('ra.action.show')} />
                </DatagridConfigurable>
            </div>
        </List>
    );
};

export const FailedBidderList = props => {
    const translate = useTranslate();
    return (
        <List
            {...props}
            actions={<ListActions />}
            perPage={25}
            sort={{ field: 'name', order: 'ASC' }}
            aside={<BidderFilterSidebar />}
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ 
                        minWidth: 'auto',
                        '& .column-financial_risk': { minWidth: '120px', maxWidth: '150px' }
                    }}
                    rowClick="show"
                    bulkActionButtons={false}
                    omit={[
                        'external_id',
                        'source',
                        'street',
                        'postal_code',
                        'country',
                        'email',
                        'website',
                        'company_type',
                        'business_start_date',
                        'business_end_date',
                        'business_suspension_date',
                        'business_resumption_date',
                        'total_wins',
                        'total_value',
                        'updated_at',
                        'last_email_sent',
                        'pkd_others',
                        'financial_risk'
                    ]}
                >
                    <TextField source="id" label={translate('resources.bidders.fields.id')} />
                    <TextField source="name" label={translate('resources.bidders.fields.name')} />
                    <StatusField source="status" label={translate('resources.bidders.fields.status')} />
                    <TypeField source="type" label={translate('resources.bidders.fields.type')} />
                    <TextField source="external_id" label={translate('resources.bidders.fields.external_id')} />
                    <TextField source="source" label={translate('resources.bidders.fields.source')} />
                    <TextField source="tax_number" label={translate('resources.bidders.fields.tax_number')} />
                    <TextField source="registration_number" label={translate('resources.bidders.fields.registration_number')} />
                    <TextField source="krs" label={translate('resources.bidders.fields.krs')} />
                    <TextField source="pkd" label={translate('resources.bidders.fields.pkd')} />
                    <TextField source="pkd_others" label="Pozostałe kody PKD" />
                    <TextField source="street" label={translate('resources.bidders.fields.street')} />
                    <TextField source="postal_code" label={translate('resources.bidders.fields.postal_code')} />
                    <TextField source="city" label={translate('resources.bidders.fields.city')} />
                    <TextField source="municipality" label={translate('resources.bidders.fields.municipality')} />
                    <TextField source="province" label={translate('resources.bidders.fields.province')} />
                    <TextField source="country" label={translate('resources.bidders.fields.country')} />
                    <EmailField source="email" label={translate('resources.bidders.fields.email')} />
                    <UrlField source="website" label={translate('resources.bidders.fields.website')} />
                    <TextField source="company_type" label={translate('resources.bidders.fields.company_type')} />
                    <DateField source="business_start_date" label={translate('resources.bidders.fields.business_start_date')} />
                    <DateField source="business_end_date" label={translate('resources.bidders.fields.business_end_date')} />
                    <DateField source="business_suspension_date" label={translate('resources.bidders.fields.business_suspension_date')} />
                    <DateField source="business_resumption_date" label={translate('resources.bidders.fields.business_resumption_date')} />
                    <NumberField source="total_wins" label={translate('resources.bidders.fields.total_wins')} />
                    <NumberField source="total_value" label={translate('resources.bidders.fields.total_value')} options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }} />
                    <FunctionField
                        source="financial_risk"
                        label="Ryzyko finansowe"
                        render={() => <BidderFinancialRiskField />}
                    />
                    <DateField source="created_at" label={translate('resources.bidders.fields.created_at')} showTime />
                    <DateField source="updated_at" label={translate('resources.bidders.fields.updated_at')} showTime />
                    <DateField source="last_email_sent" label={translate('resources.bidders.fields.last_email_sent')} showTime />
                    <ShowButton label={translate('ra.action.show')} />
                </DatagridConfigurable>
            </div>
        </List>
    );
};

export const MarketingConsentBidderList = props => {
    const translate = useTranslate();
    return (
        <StyledList 
            {...props}
            actions={<LeadsListActions />}
            perPage={25}
            sort={{ field: 'offer_date', order: 'DESC' }}
            aside={<BidderOfferLeadFilterSidebar />}
            title={translate('resources.bidders.marketing_consent', 'Oferenci z sukcesem')}
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ 
                        minWidth: 'auto',
                        '& .MuiTableCell-root': {
                            padding: '8px 12px !important',
                            verticalAlign: 'top !important',
                            minHeight: '60px !important',
                            height: 'auto !important',
                            overflow: 'visible !important'
                        },
                        '& .MuiTableRow-root': {
                            minHeight: '60px !important',
                            height: 'auto !important'
                        },
                        '& .column-id': { minWidth: '60px', maxWidth: '60px' },
                        '& .column-bidder_info': { minWidth: '180px', maxWidth: '220px' },
                        '& .column-bidder_financial_risk_rating': { minWidth: '100px', maxWidth: '120px' },
                        '& .column-bidder_notes': { minWidth: '150px', maxWidth: '250px' },
                        '& .column-tender_info': { minWidth: '220px', maxWidth: '280px' },
                        '& .column-tender_deadline': { minWidth: '140px', maxWidth: '140px' },
                        '& .column-marketing_consents': { minWidth: '180px', maxWidth: '220px' },
                        '& .column-offer_value': { minWidth: '120px', maxWidth: '140px' },
                        '& .column-status': { minWidth: '100px', maxWidth: '120px' },
                        '& .column-offer_date': { minWidth: '140px', maxWidth: '140px' },
                        '& .column-workflow_info': { minWidth: '200px', maxWidth: '280px' }
                    }}
                    rowClick={false}
                    bulkActionButtons={false}
                    omit={[
                        'bidder_id',
                        'bidder_name',
                        'bidder_tax_number', 
                        'bidder_email',
                        'bidder_registration_number',
                        'bidder_krs',
                        'bidder_pkd',
                        'bidder_city',
                        'bidder_municipality',
                        'bidder_province',
                        'tender_id',
                        'tender_notice_number',
                        'tender_title',
                        'tender_value',
                        'notes',
                        'workflow_status',
                        'workflow_error_message',
                        'workflow_current_block',
                        'bidder_financial_risk_rating', // Hidden by default, but available in column selector
                        'consent_email',
                        'consent_phone',
                        'consent_group_company'
                    ]}
                >
                    <TextField source="id" label="ID" />
                    <BidderInfoField source="bidder_info" label="Oferent" />
                    <FunctionField 
                        source="bidder_financial_risk_rating" 
                        label="Ryzyko" 
                        sortBy="bidder_financial_risk_rating"
                        render={() => <FinancialRiskField />}
                    />
                    <TenderInfoField source="tender_info" label="Przetarg" />
                    <DateField source="tender_deadline" label="Termin składania ofert" showTime />
                    <FunctionField 
                        source="marketing_consents" 
                        label="Zgody marketingowe" 
                        render={() => <MarketingConsentsField />}
                    />
                    <NumberField source="offer_value" label="Wartość oferty" options={{ style: 'currency', currency: 'PLN' }} />
                    <OfferStatusField source="status" label="Status" />
                    <DateField source="offer_date" label="Data oferty" showTime />
                    <FunctionField 
                        source="workflow_info" 
                        label="Status procesu" 
                        sortBy="workflow_status"
                        render={() => <WorkflowInfoField />}
                    />
                    <FunctionField
                        source="bidder_notes"
                        label="Notatka"
                        render={(record) => (
                            <Box sx={{ 
                                maxWidth: '250px', 
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                fontSize: '0.875rem'
                            }}>
                                {record?.bidder_notes || '-'}
                            </Box>
                        )}
                    />
                </DatagridConfigurable>
            </div>
        </StyledList>
    );
};

export const SuccessfulBidderList = props => {
    const translate = useTranslate();
    return (
        <StyledList 
            {...props}
            actions={<LeadsListActions />}
            perPage={25}
            sort={{ field: 'offer_date', order: 'DESC' }}
            aside={<BidderOfferLeadFilterSidebar />}
            title={translate('resources.bidders.successful', 'Oferenci zwycięzcy')}
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ 
                        minWidth: 'auto',
                        '& .MuiTableCell-root': {
                            padding: '8px 12px !important',
                            verticalAlign: 'top !important',
                            minHeight: '60px !important',
                            height: 'auto !important',
                            overflow: 'visible !important'
                        },
                        '& .MuiTableRow-root': {
                            minHeight: '60px !important',
                            height: 'auto !important'
                        },
                        '& .column-id': { minWidth: '60px', maxWidth: '60px' },
                        '& .column-bidder_info': { minWidth: '180px', maxWidth: '220px' },
                        '& .column-bidder_financial_risk_rating': { minWidth: '100px', maxWidth: '120px' },
                        '& .column-bidder_notes': { minWidth: '150px', maxWidth: '250px' },
                        '& .column-tender_info': { minWidth: '220px', maxWidth: '280px' },
                        '& .column-tender_deadline': { minWidth: '140px', maxWidth: '140px' },
                        '& .column-marketing_consents': { minWidth: '180px', maxWidth: '220px' },
                        '& .column-offer_value': { minWidth: '120px', maxWidth: '140px' },
                        '& .column-status': { minWidth: '100px', maxWidth: '120px' },
                        '& .column-offer_date': { minWidth: '140px', maxWidth: '140px' },
                        '& .column-workflow_info': { minWidth: '200px', maxWidth: '280px' }
                    }}
                    rowClick={false}
                    bulkActionButtons={false}
                    omit={[
                        'bidder_id',
                        'bidder_name',
                        'bidder_tax_number', 
                        'bidder_email',
                        'bidder_registration_number',
                        'bidder_krs',
                        'bidder_pkd',
                        'bidder_city',
                        'bidder_municipality',
                        'bidder_province',
                        'tender_id',
                        'tender_notice_number',
                        'tender_title',
                        'tender_value',
                        'notes',
                        'workflow_status',
                        'workflow_error_message',
                        'workflow_current_block',
                        'bidder_financial_risk_rating',
                        'consent_email',
                        'consent_phone',
                        'consent_group_company'
                    ]}
                >
                    <TextField source="id" label="ID" />
                    <BidderInfoField source="bidder_info" label="Oferent" />
                    <FunctionField 
                        source="bidder_financial_risk_rating" 
                        label="Ryzyko" 
                        sortBy="bidder_financial_risk_rating"
                        render={() => <FinancialRiskField />}
                    />
                    <TenderInfoField source="tender_info" label="Przetarg" />
                    <DateField source="tender_deadline" label="Termin składania ofert" showTime />
                    <FunctionField 
                        source="marketing_consents" 
                        label="Zgody marketingowe" 
                        render={() => <MarketingConsentsField />}
                    />
                    <NumberField source="offer_value" label="Wartość oferty" options={{ style: 'currency', currency: 'PLN' }} />
                    <OfferStatusField source="status" label="Status" />
                    <DateField source="offer_date" label="Data oferty" showTime />
                    <FunctionField 
                        source="workflow_info" 
                        label="Status procesu" 
                        sortBy="workflow_status"
                        render={() => <WorkflowInfoField />}
                    />
                    <FunctionField
                        source="bidder_notes"
                        label="Notatka"
                        render={(record) => (
                            <Box sx={{ 
                                maxWidth: '250px', 
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                fontSize: '0.875rem'
                            }}>
                                {record?.bidder_notes || '-'}
                            </Box>
                        )}
                    />
                </DatagridConfigurable>
            </div>
        </StyledList>
    );
};

export const LeadyBidderList = props => {
    const translate = useTranslate();
    return (
        <List
            {...props}
            actions={<ListActions />}
            perPage={25}
            sort={{ field: 'name', order: 'ASC' }}
            aside={<BidderFilterSidebar />}
        >
            <div style={{ width: '100%', overflowX: 'auto' }}>
                <DatagridConfigurable 
                    sx={{ minWidth: 'auto' }}
                    rowClick="show"
                    bulkActionButtons={false}
                    omit={[
                        'external_id',
                        'source',
                        'street',
                        'postal_code',
                        'country',
                        'email',
                        'website',
                        'company_type',
                        'business_start_date',
                        'business_end_date',
                        'business_suspension_date',
                        'business_resumption_date',
                        'total_wins',
                        'total_value',
                        'updated_at',
                        'last_email_sent',
                        'pkd_others'
                    ]}
                >
                    <TextField source="id" label={translate('resources.bidders.fields.id')} />
                    <TextField source="name" label={translate('resources.bidders.fields.name')} />
                    <StatusField source="status" label={translate('resources.bidders.fields.status')} />
                    <TypeField source="type" label={translate('resources.bidders.fields.type')} />
                    <TextField source="external_id" label={translate('resources.bidders.fields.external_id')} />
                    <TextField source="source" label={translate('resources.bidders.fields.source')} />
                    <TextField source="tax_number" label={translate('resources.bidders.fields.tax_number')} />
                    <TextField source="registration_number" label={translate('resources.bidders.fields.registration_number')} />
                    <TextField source="krs" label={translate('resources.bidders.fields.krs')} />
                    <TextField source="pkd" label={translate('resources.bidders.fields.pkd')} />
                    <TextField source="pkd_others" label="Pozostałe kody PKD" />
                    <TextField source="street" label={translate('resources.bidders.fields.street')} />
                    <TextField source="postal_code" label={translate('resources.bidders.fields.postal_code')} />
                    <TextField source="city" label={translate('resources.bidders.fields.city')} />
                    <TextField source="municipality" label={translate('resources.bidders.fields.municipality')} />
                    <TextField source="province" label={translate('resources.bidders.fields.province')} />
                    <TextField source="country" label={translate('resources.bidders.fields.country')} />
                    <EmailField source="email" label={translate('resources.bidders.fields.email')} />
                    <UrlField source="website" label={translate('resources.bidders.fields.website')} />
                    <TextField source="company_type" label={translate('resources.bidders.fields.company_type')} />
                    <DateField source="business_start_date" label={translate('resources.bidders.fields.business_start_date')} />
                    <DateField source="business_end_date" label={translate('resources.bidders.fields.business_end_date')} />
                    <DateField source="business_suspension_date" label={translate('resources.bidders.fields.business_suspension_date')} />
                    <DateField source="business_resumption_date" label={translate('resources.bidders.fields.business_resumption_date')} />
                    <NumberField source="total_wins" label={translate('resources.bidders.fields.total_wins')} />
                    <NumberField source="total_value" label={translate('resources.bidders.fields.total_value')} options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }} />
                    <DateField source="created_at" label={translate('resources.bidders.fields.created_at')} showTime />
                    <DateField source="updated_at" label={translate('resources.bidders.fields.updated_at')} showTime />
                    <DateField source="last_email_sent" label={translate('resources.bidders.fields.last_email_sent')} showTime />
                    <ShowButton label={translate('ra.action.show')} />
                </DatagridConfigurable>
            </div>
        </List>
    );
}; 